import {defineStore} from 'pinia'
import {v4 as uuidv4} from 'uuid'
import Konva from 'konva'
import {cloneDeep, isEqual, uniqBy} from 'lodash'

// import Snackbar
import {useSnackbarStore} from '@/store/snackbar'

// import API
import {
    $drawingMapDeleteItems,
    $drawingMapSaveItems,
    $getCampaignLastDrawings,
    $getDrawing,
    $updateDrawing
} from '@/api/campaigns'
import {
    $createOfflineItems,
    $createSign,
    $duplicateDrawingSign,
    $multipleUpdateSignMapSnapshots,
    $updateSign
} from '@/api/signs'
import {$createKopItem, $updateKopItem} from '@/api/kop'

// import helpers
import {addDrawingItem, updateDrawingItem} from '@/store/helpers/drawItemsHelpers'
import {
    getSignTypesWithImg,
    getUpdatedLocationImageUrlRecursively,
    loadDefaultLocationsFromLocalStorage,
    loadDefaultSignTypesFromLocalStorage,
    saveDefaultLocationsToLocalStorage,
    saveDefaultSignTypesToLocalStorage,
} from '@/store/helpers/offlineItems/main'
import {idbClearAllImages} from '@/store/helpers/offlineItems/indexedDB'
import {transformDrawingsArrayToObj} from '@/store/helpers/offlineItems/utils'
import {updateItemsLonLat} from '@/store/helpers/update-items-lon-lat'
import {getLocationById} from '@/helpers/wayfinding/locations/getLocationById'
import {loadImage} from '@/store/helpers/utils/loadItemImage'
import {getUngroupedItems} from '@/store/helpers/utils/getUngroupedItems'
import {normalizeDrawingItemPointsXY} from '@/store/helpers/utils/normalizeDrawingItemPointsXY'
import {getMapSnapshot} from '@/helpers/drawing/getMapSnapshot'
import {getCurrentDateTimeStr} from '@/helpers/utils/getCurrentDateTimeStr'
import {waitForKonvaImagesToLoad} from '@/helpers/drawing/waitForKonvaImagesToLoad'
import {addAnchorToLine as wfRouteAddAnchorToLine} from '@/helpers/drawing/routes/addAnchorToLine'
import {handleWFRouteDeletions} from '@/helpers/drawing/routes/handleWFRouteDeletions'
import {
    handleFullRebuildWFRouteWithNewSignsOrder
} from '@/helpers/drawing/routes/handleFullRebuildWFRouteWithNewSignsOrder'
import {handleWayfindingSignFilters} from '@/helpers/wayfinding/signs/handleWayfindingSignFilters'

// Types
import type {SignCreatePayload} from '@/store/models/sign'
import type {KopItemCreatePayload} from '@/models/kop/drawingStore'
import type {DrawingHistoryItem} from '@/models/store/DrawingHistory'

const signData = (id: string, x: number, y: number, sign: any, height: number) => {
    // const image = new window.Image()
    // image.src = sign.source
    const tagX = x + 10
    const tagY = y + 12
    return {
        type: 'sign',
        id: id,
        x: x,
        y: y,
        tagX: tagX,
        tagY: tagY,
        height,
        width: 25,
        opacity: 1,
        draggable: true,
        file: sign.source,
        sign: null
    }
}

const kopItemData = async (id: string, x: number, y: number, kopItemType: any) => {
    const baseKopItemData = {
        type: 'kop_item',
        id: id,
        opacity: 1,
        draggable: true,
        fillPatternRepeat: 'repeat-x',
        shape_items: kopItemType.shape,
        kop_item_type: kopItemType?.id || null,
        kop_item_type_form: kopItemType?.form_type || 1,
        kop_item: null,
        kop_item_shape_image: kopItemType.shape_image,
        kop_item_shape_width: kopItemType.shape_width,
        kop_item_shape_height: kopItemType.shape_height
    }

    if (baseKopItemData.kop_item_type_form === 1) {
        return {
            ...baseKopItemData,
            points: [x, y],
            initial_points: [x, y]
        }
    } else {
        return {
            ...baseKopItemData,
            x: x,
            y: y
        }
    }
}

const restoredKopItemData = (id: string, kopItem: any) => {
    const shapeWidth = kopItem?.kop_item_type_info?.shape_width || 0
    const shapeHeight = kopItem?.kop_item_type_info?.shape_height || 0
    const points = [
      parseFloat(kopItem.left), parseFloat(kopItem.top),
      parseFloat(kopItem.left + shapeWidth), parseFloat(kopItem.top),
      parseFloat(kopItem.left + shapeWidth), parseFloat(kopItem.top + shapeHeight),
      parseFloat(kopItem.left), parseFloat(kopItem.top  + shapeHeight),
    ]

    return {
        type: 'kop_item',
        id: id,
        rotation: kopItem.direction,
        opacity: 1,
        draggable: true,
        fillPatternRepeat: 'repeat-x',
        points: points,
        initial_points: [parseFloat(kopItem.left), parseFloat(kopItem.top)],
        shape_items: kopItem.kop_item_type_info.shape,
        kop_item: kopItem.id,
        kop_item_shape_image: kopItem.kop_item_type_info.shape_image,
        kop_item_shape_width: shapeWidth,
        kop_item_shape_height: shapeHeight
    }
}

const labelData = (id: string, x: number, y: number, parent: any) => {
    return {id: id, type: 'label', x: x, y: y, parent: parent, width: 100, height: 40,}
}

const restoredSignData = async (id: string, sign: any) => {
    const { width, height } = await loadImage(sign?.type_info?.source)
    const ratio = width / height

    // const image = new window.Image()
    // image.src = sign.source
    const tagX = parseFloat(sign.left) + 10
    const tagY = parseFloat(sign.top) + 12
    return {
        type: 'sign',
        id: id,
        x: parseFloat(sign.left),
        y: parseFloat(sign.top),
        tagX: tagX,
        tagY: tagY,
        height: 25 / ratio,
        width: 25,
        opacity: 1,
        draggable: true,
        file: sign.type_info ? sign.type_info.source : '',
        sign: sign.id,
    }
}

export const useDrawingStore = defineStore({
    id: 'drawing',
    persist: {
        paths: ['items', 'offlineSigns', 'geoPoints']
    },
    state: () => ({
        stage: null as any,
        pdfStageLayerKey: 10000 as number,
        isEditKopItemTypeShape: false as boolean,
        darkMode: false,
        colorFilters: {
            grayscale: 0,
            brightness: 100,
            contrast: 100,
            opacity: 100
        },
        gridModeSettings: {
            isEnabled: false,
            gridOpacity: 1,
            gridSize: 10,
            gridColour: '#ddd'
        },
        offlineMode: false,
        offlineModeObjectUrls: [] as string[],  // List to store object URLs
        handleOfflineModeLoading: false as boolean,  // List to store object URLs
        allDefaultDrawingItems: {} as any,  // All drawing items by all locations { "locationId": "items[]" }
        showLabels: false,  // Wayfinding Sign Labels view
        showDimensions: false,  // KOP Item Dimensions view
        showItemCodes: false,  // KOP Item Codes view
        isHistoryPreviewMode: false,
        project: {} as any,
        currentDrawingId: null as null | number,
        currentDrawingPixelsPerMeterScale: 0 as number,
        currentDrawingSetScaleLineConfig: null as any,
        initialItems: [] as any[],  // last DB actual items
        items: [] as any[],
        copiedItems: [] as any[], // Temporary store for copied items
        tempItems: [] as any[], // Temporary store for items (e.g. during Set Scale operations)
        isKopItemCreationMode: false as boolean, // Individual/Distance KOP Item Group creation mode
        existingKopItemInEditMode: null as null | any, // Existing Individual/Distance KOP Item in edit mode
        pendingKopItems: [] as any[], // Temporary store for Individual/Distance KOP items (for KOP Item Group creation)
        offlineItems: [] as any[],  //  offline Items - all(except Signs/Sign Labels), lines, text, etc.
        offlineSigns: [] as any[],  // offline Signs
        offlineSignLabels: [] as any[],  // offline Sign Labels
        uploadOfflineSignsLoading: false as boolean,
        specificItemIdToShow: null as number | null,
        selectedBtn: 'lock',
        selectedItem: null as any,
        selectedItems: [] as any,
        copiedSignId: null as any,
        signIdsToCopy: [] as any[], // Array of sign IDs to duplicate
        duplicateLoading: false as boolean, // Loading state
        drawingItem: null as any,
        selectedSignType: null as any,
        selectedSignIds: [] as any,
        selectedKopItemIds: [] as any,
        signDialogVisible: false as boolean,
        kopItemDialogVisible: false as boolean,
        drawingItemConfigVisible: true as boolean,
        hoveredItem: null as any,
        signTypes: [] as any,
        signStatuses: [] as any,
        funcAreas: [] as any,
        locations: {} as any,
        selectedLocation: null as any,
        selectedFilterSignStatuses: [] as number[],
        selectedFilterSignTypes: [] as number[],
        selectedFilterSignFunctionalAreas: [] as number[],
        signSearchFilter: '' as string,
        signSearchByFilter: 'sign_code' as 'sign_code' | 'sign_glossary' | 'sign_glossary_pictogram' | 'fa',
        selectedSignIdsFilter: [] as number[],
        selectedFilterKopItemStatuses: [] as number[],
        selectedFilterKopItemTypes: [] as number[],
        selectedFilterKopItemFunctionalAreas: [] as number[],
        mapLoading: false,
        drawingLoading: false,
        locationSetupLoading: false,
        updateAllSignMapSnapshotsLoading: false,
        loadDrawingMapImgTrigger: 0, // loadMapImg on trigger > 0
        transformer: null as any,
        fullscreen: false,
        isDrawing: false,
        selectedArea: null as any,
        sessionKey: Math.random(),
        mousePos: {
            x: 0,
            y: 0,
        },
        bgImg: {image: null, x: 0, y: 0, width: 0, height: 0, filters: [Konva.Filters.Grayscale]}, // Konva bg image
        width: 0, // Width of the rendered Map, including scale. Set to 0 by default.
        height: 0, // Height of the rendered Map, including scale. Set to 0 by default.
        scale: {
            x: 1,
            y: 1,
        },
        transformStartScale: {
            x: 1,
            y: 1,
        },
        initialZoom: null as null | number,
        geoMapKey: 5000 as number,
        isGeoSetMode: false,
        isDrawingScaleSetMode: false,
        setScaleModeLineSize: 1,
        geoMapSetModeOpacity: 0.5 as number,
        isGeoMapMode: false,
        isGeoDrawingMode: false,
        geoPoints: [] as any[],
        pdfCornersGeo: [] as any[],
        mapCenter: [0, 0] as number[],
        geoMapBearing: 0 as number,
        selectedFARouteId: null as null | number | string,
        isSetRouteMode: false,
        isShowLastSavedVersionRouteMode: false,  // if active - initialFuncAreaRoutes visible only
        funcAreaRoutes: [] as any[],
        initialFuncAreaRoutes: [] as any[],
        isShowFunAreaRouteAnchors: false,
        showRemoveSignFromRouteConfirmDialog: false,
        selectedSignToRemoveFromRoute: null,
        // KOP Project
        kopItemTypes: [] as any[],
        selectedKopItemType: null as null | any,

        // HISTORY (UNDO/REDO)
        drawingHistory: [] as DrawingHistoryItem[][], // Track id, position and size
        drawingHistoryStep: 0, // Track the current position in undo/redo history, start from 0

        // Draw POLYGON
        polygon: {
            points: [] as Array<{ x: number; y: number }>,
            nextPoint: { x: 0, y: 0 }
        },
    }),
    getters: {
        isInactiveProject: state => state.project?.is_visible === false,
        ungroupedItems: (state) => {
            return getUngroupedItems(state.items)
        },
        isEditMode: state => ['lock', 'move'].includes(state.selectedBtn),
        isMoveMode: state => state.selectedBtn === 'move',
        zoom: state => Math.round(state.scale.x * 100),
        locationMap: state => {
            if (state.selectedLocation) {
                return state.selectedLocation.modified_image
            } else if (state.project) {
                return state.project.location_modified_image
            } else {
                return null
            }
        },
        isGeoMapVisible: state => (state.isGeoSetMode || state.isGeoMapMode || state.isGeoDrawingMode ||
          state.isDrawingScaleSetMode),
        selectedFARouteIdx: state => state.funcAreaRoutes.findIndex((i: any) => i.id === state.selectedFARouteId),
        // Getter to get the changed item ids
        changedItemIds(state) {
            // Remove Route Items from comparison
            const filteredInitialItems = state.initialItems.filter(item => {
                return item?.id && !['route', 'anchor'].includes(item?.tag)
            })
            const filteredCurrentItems = state.items.filter(item => {
                return item?.id && !['route', 'anchor'].includes(item?.tag)
            })

            return filteredInitialItems
              .map(initialItem => {
                  // Find the item with the same id in the current state
                  const currentItem = filteredCurrentItems.find(item => item?.id === initialItem?.id)

                  // Use Lodash's cloneDeep to create deep copies and remove 'draggable' field
                  const cleanInitialItem = cloneDeep(initialItem)
                  const cleanCurrentItem = cloneDeep(currentItem)

                  // Remove the 'draggable' field from both items
                  delete cleanInitialItem?.draggable
                  delete cleanCurrentItem?.draggable

                  // Compare the cleaned items using Lodash's isEqual
                  return currentItem && !isEqual(cleanCurrentItem, cleanInitialItem) ? initialItem?.id : null
              })
              .filter(id => !!id) // Filter out falsy values
        },

        // Getter to get newly created item ids
        newlyCreatedItemIds(state) {
            return state.items
              .filter(item => {
                  // Check if item does not exist in initialItems and if the item's type is not sign/kop_item/label
                  const existsInInitial = state.initialItems.some(initialItem => initialItem?.id === item?.id)
                  // Check for new items (with correct Item ID)
                  return item?.id && !existsInInitial && !['sign', 'kop_item', 'label', 'group'].includes(item?.type) &&
                    item?.id !== 'set-scale-measure-line' && !['route', 'anchor'].includes(item?.tag)
              })
              .map(item => item?.id) // Return only the ids of new items
        },

        isChangedWFRoutes: (state) => {
            const currentRoutes =
              state.funcAreaRoutes.map(({ is_shown, is_shown_route_info_only, ...rest }) => rest)
            const initialRoutes =
              state.initialFuncAreaRoutes.map(({ is_shown, is_shown_route_info_only, ...rest }) => rest)

            return !isEqual(currentRoutes, initialRoutes)
        },

        selectedSigns: (state) => {
            return state.selectedItems.filter((i: any) => i.type === 'sign' && !!i.sign)
        },
    },
    actions: {
        addPolygonPoint(point: { x: number; y: number }) {
            this.polygon.points.push(point)
        },

        updatePolygonNextPoint(point: { x: number; y: number }) {
            this.polygon.nextPoint = point
        },

        updateSpecificItemIdToShow(specificItemIdToShow: number | null) {
            this.specificItemIdToShow = specificItemIdToShow
        },

        updateDrawingItemConfigVisible(drawingItemConfigVisible: boolean) {
            this.drawingItemConfigVisible = drawingItemConfigVisible
        },

        updateIsShowFunAreaRouteAnchors(isShowFunAreaRouteAnchors: boolean) {
            this.isShowFunAreaRouteAnchors = isShowFunAreaRouteAnchors
        },

        completePolygon() {
            if (this.polygon.points.length > 2) {
                const pointsArray = this.polygon.points.flatMap((p) => [p.x, p.y])
                const id = uuidv4()

                const strokeWidth = this.isEditKopItemTypeShape ? (2 / (this.initialZoom || 1)) : 2

                const polygonItem = {
                    id: id,
                    type: 'polygon',
                    points: pointsArray,
                    stroke: 'black',
                    strokeWidth: strokeWidth,
                    opacity: 1,
                    closed: true,
                    draggable: true,
                    scaleX: 1,
                    scaleY: 1,
                    x: 0,
                    y: 0
                }

                normalizeDrawingItemPointsXY(polygonItem)

                this.items.push(polygonItem)

                this.clearPolygonDraw()
            }
        },

        clearSelectedItems() {
            this.selectedItem = null
            this.selectedItems = []
        },

        resetColorFilters() {
          this.colorFilters = {
            grayscale: 0,
            brightness: 100,
            contrast: 100,
            opacity: 100
          }
        },

        resetKopColorFilters() {
            this.colorFilters = {
                grayscale: 30,
                brightness: 100,
                contrast: 100,
                opacity: 35
            }
        },

        clearPolygonDraw() {
            this.polygon = {
                points: [],
                nextPoint: { x: 0, y: 0 }
            }
        },
        updatePdfStageKey() {
            this.pdfStageLayerKey = this.pdfStageLayerKey + 1
        },
        updateIsEditKopItemTypeShape(isEditKopItemTypeShape: boolean) {
            this.isEditKopItemTypeShape = isEditKopItemTypeShape
        },
        updateCurrentDrawingParams(
          currentDrawingId: null | number,
          currentDrawingSetScaleLineConfig: any,
          currentDrawingPixelsPerMeterScale: number
        ) {
            this.currentDrawingId = currentDrawingId
            this.currentDrawingSetScaleLineConfig = currentDrawingSetScaleLineConfig
            this.currentDrawingPixelsPerMeterScale = currentDrawingPixelsPerMeterScale
        },
        updateInitialItems(initialItems: any[]) {
            this.initialItems = JSON.parse(JSON.stringify(initialItems))
        },
        selectLastSignItem() {
            let lastSignItemId = null

            if (!this.items?.length) return

            for (let i = this.items?.length - 1; i >= 0; i--) {
                if (this.items[i]?.type === 'sign' && !!this.items[i]?.sign) {
                    lastSignItemId = this.items[i].sign
                    break
                }
            }

            if (!lastSignItemId) return

            this.selectItem(lastSignItemId, true)
        },
        getNewAndChangedItems() {
            // Filter 'this.items' to return only those items whose 'id' is in 'newlyCreatedItemIds'
            const newlyCreatedItems = this.items.filter(item =>
              // Check if the current item's 'id' exists in 'newlyCreatedItemIds' array
              this.newlyCreatedItemIds.includes(item.id)
            )

            // Filter 'this.items' to return only those items whose 'id' is in 'changedItemIds'
            const changedItems = this.items.filter(item =>
              // Check if the current item's 'id' exists in 'changedItemIds' array
              this.changedItemIds.includes(item.id)
            )

            return { newlyCreatedItems, changedItems }
        },
        // Handle the selection of a new location
        async onSelectLocation() {
            // Retrieve newly created items and items that have changed
            const { newlyCreatedItems, changedItems } = this.getNewAndChangedItems()

            // Check if there are any newly created or changed items
            // If there are, save the items before switching to the new location
            if (newlyCreatedItems.length || changedItems.length) {
                await this.saveItems()  // Save the current items to persist changes
            }

            // Clear Drawing History
            this.clearDrawingHistory()
        },
        updateLocationSetupLoading(locationSetupLoading: boolean) {
            this.locationSetupLoading = locationSetupLoading
        },
        updateLoadDrawingMapImgTrigger(loadDrawingMapImgTrigger: number) {
            this.loadDrawingMapImgTrigger = loadDrawingMapImgTrigger
        },
        clearDrawingHistory() {
            this.drawingHistory = []
            this.drawingHistoryStep = 0
        },
        clearDrawingBgImg() {
            this.bgImg.image = null
        },
        clearWayfindingDrawingTopBar() {
            this.selectedBtn = 'lock'
            this.selectedSignType = null
        },
        clearKopDrawingTopBar() {
            this.selectedBtn = 'lock'
            this.selectedKopItemType = null
        },
        clearSignDrawingSidebarFilters() {
            this.selectedFilterSignStatuses = []
            this.selectedFilterSignTypes = []
            this.selectedFilterSignFunctionalAreas = []
            this.signSearchFilter = ''
            this.signSearchByFilter = 'sign_code'
            this.selectedSignIdsFilter = []
        },
        clearKopDrawingSidebarFilters() {
            this.selectedFilterKopItemStatuses = []
            this.selectedFilterKopItemTypes = []
            this.selectedFilterKopItemFunctionalAreas = []
        },
        clearKopItemCreationMode() {
            this.isKopItemCreationMode = false
            this.existingKopItemInEditMode = null
            this.pendingKopItems = []

            // Clear selected Items
            this.clearSelectedItems()
        },
        turnOnEditModeExistingKopItem(existingKopItem: any) {
            if (!existingKopItem?.id || !existingKopItem?.kop_item_type) {
                return
            }

            // Update Top Bar settings
            this.selectBtn('move')

            if (this.kopItemTypes.map(i => i.id).includes(existingKopItem?.kop_item_type)) {
                this.selectedKopItemType = this.kopItemTypes.find(i => i.id === existingKopItem?.kop_item_type)
            } else {
                useSnackbarStore().showMessage({
                    color: 'warning',
                    icon: 'mdi-alert-circle',
                    title: 'Warning',
                    text: 'Required KOP Item Type is missing. Check Project Settings and try again.'
                })
                return
            }

            // Deep copy
            this.existingKopItemInEditMode = JSON.parse(JSON.stringify(existingKopItem))

            this.isKopItemCreationMode = true

            const parentX = existingKopItem?.x || 0
            const parentY = existingKopItem?.y || 0

            // Convert child relative positions and label coordinates(if exist) to absolute positions
            this.pendingKopItems = (existingKopItem.children || []).map((child: any) => {
                const kopItemChild = {
                    ...child,
                    x: (child.x || 0) + parentX,
                    y: (child.y || 0) + parentY
                }
                if (kopItemChild?.realWidthLabelX || kopItemChild?.realWidthLabelY) {
                    kopItemChild.realWidthLabelX = (kopItemChild?.realWidthLabelX || 0) + parentX
                    kopItemChild.realWidthLabelY = (kopItemChild?.realWidthLabelY || 0) + parentY
                }
                if (kopItemChild?.kopItemCodeLabelX || kopItemChild?.kopItemCodeLabelY) {
                    kopItemChild.kopItemCodeLabelX = (kopItemChild?.kopItemCodeLabelX || 0) + parentX
                    kopItemChild.kopItemCodeLabelY = (kopItemChild?.kopItemCodeLabelY || 0) + parentY
                }
                return kopItemChild
            })

            // Replace the parent group with individual children
            this.items = this.items
              .filter(item => item?.id !== existingKopItem.id)
              .concat(this.pendingKopItems)

            // Clear selected Items
            this.clearSelectedItems()
        },
        async updateExistingKopItem() {
            if (!this.existingKopItemInEditMode?.id || !this.existingKopItemInEditMode?.kop_item) {
                useSnackbarStore().showMessage({
                    color: 'warning',
                    icon: 'mdi-alert-circle',
                    title: 'Warning',
                    text: 'Required KOP Item ID is missing. Please refresh and try again.'
                })
                return
            }

            const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

            const pendingKopItemIds = new Set(this.pendingKopItems.map((kopItem: any) => kopItem.id))
            const newKopItemChildren = this.items.filter((item: any) => pendingKopItemIds.has(item.id))

            const payload = {
                // Mark as Update Drawing KOP Item
                is_update_drawing_kop_item: true,
                // New Drawing KOP Item Children with same KOP Item ID
                drawing_kop_item_data: {
                  id: this.existingKopItemInEditMode.id,
                  children: newKopItemChildren
                },
                // Changed and not saved items for data consistency
                updated_drawing_items: JSON.stringify(changedItems),
                // Unsaved drawing items to save before KOP Item delete for data consistency
                unsaved_drawing_items: JSON.stringify(unsavedItems),
            }

            // return Actual Drawing with updated KOP Item
            const res = await $updateKopItem(this.existingKopItemInEditMode?.kop_item, payload)

            // Update current items with new Drawing Items
            this.setItems(res.data.modified_items)

            // Update current Drawing params
            this.updateCurrentDrawingParams(
              res.data.id,
              res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
              res?.data?.pixels_per_meter_scale || 0
            )

            // Update initial items
            this.updateInitialItems(res.data.modified_items)

            // Reset state
            this.clearKopItemCreationMode()
        },
        cancelEditModeExistingKopItem() {
            if (!this.existingKopItemInEditMode) {
                return
            }

            const pendingItemIds = new Set(this.pendingKopItems?.map(item => item.id) || [])

            // Filter out pending items and restore original
            this.items = [
                ...this.items.filter(item => !pendingItemIds.has(item.id)),
                this.existingKopItemInEditMode
            ]

            // Reset state
            this.clearKopItemCreationMode()
        },
        clearItems() {
          this.items = []
        },
        setItems(items: any[]) {
            this.items = items
            this.lockMoving()

            this.updateSignRoutesVisibility()
        },
        async saveItems(
          newGroupItems:any = [],
          {
              setScaleLineConfig = null,
              pixelsPerMeterScale = 0
          }: {
              setScaleLineConfig?: string | null,
              pixelsPerMeterScale?: number
          } = {}
          ) {
            const location = this.selectedLocation ? this.selectedLocation.id : this.project.location

            // Wayfinding or KOP
            const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

            interface SaveItemsPayload {
                campaign_id: any;
                location_id: any;
                updated_drawing_items: string;
                unsaved_drawing_items: string;
                new_group_items: string;
                geo_points: any[];
                fa_routes: any[];
                pixels_per_meter_scale?: number;
                set_scale_line_config?: string | null;
            }

            const payload: SaveItemsPayload = {
                campaign_id: this.project.id,
                location_id: location,
                // Changed and not saved items for data consistency
                updated_drawing_items: JSON.stringify(changedItems),
                // Unsaved drawing items to save before Sign/Item delete for data consistency
                unsaved_drawing_items: JSON.stringify(unsavedItems),
                // New group items after group/ungroup actions
                new_group_items: JSON.stringify(newGroupItems),
                geo_points: this.geoPoints,
                fa_routes: this.funcAreaRoutes,
            }

            if (pixelsPerMeterScale) {
                payload.pixels_per_meter_scale = pixelsPerMeterScale || 0
            }
            if (pixelsPerMeterScale) {
                payload.set_scale_line_config = setScaleLineConfig || null
            }

            // return Actual Drawing Data
            const res = await $drawingMapSaveItems(payload)

            // Update current Drawing params
            this.updateCurrentDrawingParams(
              res.data.id,
              res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
              res?.data?.pixels_per_meter_scale || 0
            )

            // Update initial items
            this.updateInitialItems(res.data.modified_items)

            return res
        },

        async getDrawing(isLoading = true, isClearItems = false) {
            if (isLoading) {
                this.drawingLoading = true
            }

            if (isClearItems) {
                this.clearItems()
            }
            try {
                const location = this.selectedLocation ? this.selectedLocation.id : this.project?.location

                const resDrawing = await $getDrawing({
                    project: this.project.id,
                    location: location,
                    last: true // get only the last drawing
                })
                if (resDrawing.data.results.length) {
                    // Update current Drawing params
                    this.updateCurrentDrawingParams(
                      resDrawing.data.results[0].id,
                      resDrawing?.data?.results?.[0]?.set_scale_line_config ? JSON.parse(resDrawing?.data?.results?.[0]?.set_scale_line_config) : null,
                      resDrawing?.data?.results?.[0]?.pixels_per_meter_scale || 0
                    )

                    // Update initial items
                    this.updateInitialItems(resDrawing.data.results[0].modified_items)

                    // items - all signs, modified_items - signs with sign types and functional areas
                    this.setItems(resDrawing.data.results[0].modified_items)
                    // Update funcAreaRoutes with last drawing info
                    this.setFuncAreaRoutes(resDrawing.data.results[0].fa_routes)
                    // Update geoPoints with last drawing main location info
                    this.addGeoPoints(resDrawing.data.results[0].geo_points)
                } else {
                    // Update current Drawing params
                    this.updateCurrentDrawingParams(
                      null,
                      null,
                      0
                    )

                    // Update initial items
                    this.updateInitialItems([])

                    this.setItems([])
                    this.setFuncAreaRoutes([])
                    this.addGeoPoints([])
                }
            } catch (e) {
                console.log(e)
            } finally {
                if (isLoading) {
                    this.drawingLoading = false
                }
            }
        },

        setZoom(scale: number) {
            this.scale.x = Number(scale.toFixed(1))
            this.scale.y = Number(scale.toFixed(1))

            this.height = (this.bgImg.height) * this.scale.x
            this.width = this.bgImg.width * this.scale.x

            // Center map position on setZoom
            const stage = this.stage?.getStage()
            if (!stage) return

            const pdfWrapper = document.getElementById('map-wrapper')
            const pdfWrapperWidth = pdfWrapper?.offsetWidth || 0
            const pdfWrapperHeight = pdfWrapper?.offsetHeight || 0

            const centerX = (this.width - pdfWrapperWidth) / 2
            const centerY = (this.height - pdfWrapperHeight) / 2

            stage.position({ x: -centerX, y: -centerY })
        },
        increaseZoom() {
            this.scale.x += 0.05
            this.scale.y += 0.05

            this.height = (this.bgImg.height) * this.scale.x
            this.width = this.bgImg.width * this.scale.x
        },
        decreaseZoom() {
            if (this.scale.x > 0.1) {
                this.scale.x -= 0.05
                this.scale.y -= 0.05

                this.height = (this.bgImg.height) * this.scale.x
                this.width = this.bgImg.width * this.scale.x
            }
        },
        handleStageZoom(newScale: number) {
            this.scale.x = newScale
            this.scale.y = newScale

            this.height = (this.bgImg.height) * newScale
            this.width = this.bgImg.width * newScale
        },
        findItemById(itemId: number) {
            if (!itemId) return

            return this.items.find(i => i.id === itemId)
        },
        findOfflineItemById(item: any) {
            return this.offlineSigns.find(i => i.id === item.attrs.id)
        },
        findItemBySign(signParam: any) {
            return this.items.find(i => i.sign === parseInt(signParam))
        },

        async deleteSelectedItem() {
            const selectedItemIds = this.selectedItems.map((item: any) => item.id)
            // If no project - Kop Item Type Create/Edit
            if (!this.project?.id) {
                this.items = this.items.filter(item => !selectedItemIds.includes(item.id))
            }
            // If KOP Creation Mode - remove selected KOP Item Children
            if (this.project?.id && this.isKopItemCreationMode) {
                this.items = this.items.filter(item => !selectedItemIds.includes(item.id))
                this.pendingKopItems = this.pendingKopItems.filter(item => !selectedItemIds.includes(item.id))
            }
            // If Set Route Mode - remove selected Item
            else if (this.project?.id && this.isSetRouteMode) {
                // Update FA Route Items and lines
                const currentFARoute = this.funcAreaRoutes?.[this.selectedFARouteIdx]
                const currentFARouteItems = currentFARoute?.signsOrder || []
                const filteredCurrentFARouteItems =
                  currentFARouteItems?.filter((item: any) => !selectedItemIds.includes(item.id))

                this.items = this.items.filter((item: any) => !selectedItemIds.includes(item.id))

                if (filteredCurrentFARouteItems?.length < currentFARouteItems?.length) {
                    this.changeRouteSignsOrder(currentFARouteItems, filteredCurrentFARouteItems)
                }
            }
            else if (!this.offlineMode) {
                const itemsToDelete = []
                for (const selectedItem of this.selectedItems) {
                    if (selectedItem && selectedItem?.type === 'sign' && selectedItem.sign) {
                        itemsToDelete.push({
                            id: selectedItem.id,
                            type: 'sign',
                            sign: selectedItem.sign,
                            top: selectedItem.y,
                            left: selectedItem.x
                        })
                    }
                    else if (selectedItem && selectedItem?.type === 'kop_item' && selectedItem.kop_item) {
                        itemsToDelete.push({
                            id: selectedItem.id,
                            type: 'kop_item',
                            kop_item: selectedItem.kop_item,
                            top: selectedItem.y,
                            left: selectedItem.x,
                            shape_items: {
                                points: selectedItem?.points || [],
                                fillPatternX: selectedItem?.fillPatternX || 0,
                                fillPatternY: selectedItem?.fillPatternY || 0,
                                scaleX: selectedItem?.scaleX || 1,
                                scaleY: selectedItem?.scaleY || 1,
                            },
                            direction: selectedItem?.rotation || 0,
                            // TODO: Update here after Backend update(KopItem model - scale_x scale_y fields)
                            // scale_x: selectedItem?.scaleX || 1,
                            // scale_y: selectedItem?.scaleY || 1,
                        })
                    }
                    else {
                        itemsToDelete.push({ id: selectedItem.id, type: selectedItem?.type })
                    }
                }

                const location = this.selectedLocation ? this.selectedLocation.id : this.project.location

                const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

                const payload = {
                    campaign_id: this.project.id,
                    location_id: location,
                    items_to_delete: JSON.stringify(itemsToDelete),
                    // Changed and not saved items for data consistency
                    updated_drawing_items: JSON.stringify(changedItems),
                    // Unsaved drawing items to save before Sign/Item delete for data consistency
                    unsaved_drawing_items: JSON.stringify(unsavedItems)
                }

                // return Actual Drawing with newly added Sign/KopItem
                const res = await $drawingMapDeleteItems(payload)

                // Update current items with new Drawing Items
                this.setItems(res.data.modified_items)

                // Update current Drawing params
                this.updateCurrentDrawingParams(
                  res.data.id,
                  res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
                  res?.data?.pixels_per_meter_scale || 0
                )

                // Update initial items
                this.updateInitialItems(res.data.modified_items)
            } else {
                this.items = this.items.filter(item => !selectedItemIds.includes(item.id) && !(item?.type === 'label' && selectedItemIds.includes(item.parent)))
                this.offlineItems = this.offlineItems.filter(item => !selectedItemIds.includes(item.id))
                this.offlineSigns = this.offlineSigns.filter(item => !selectedItemIds.includes(item.id))
                this.offlineSignLabels = this.offlineSignLabels.filter(item => !selectedItemIds.includes(item.parent))
            }

            // Clear selected Items
            this.clearSelectedItems()
        },

        async restoreDeletedSign(signId: string | number, signType: any) {
            const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

            const { width, height } = await loadImage(signType?.source)
            const ratio = width / height

            const payload = {
                is_removed: false,
                // Drawing Sign Data to restore sign + create new drawing with newly restored sign and return
                drawing_sign_data: JSON.stringify({
                    width: 25,
                    height: 25 / ratio,
                }),
                // Changed and not saved items for data consistency
                updated_drawing_items: JSON.stringify(changedItems),
                // Unsaved drawing items to save before Sign delete for data consistency
                unsaved_drawing_items: JSON.stringify(unsavedItems),
            }

            // return Actual Drawing with newly restored Sign
            const res = await $updateSign(signId, payload)

            // Update current items with new Drawing Items
            this.setItems(res.data.modified_items)

            // Update current Drawing params
            this.updateCurrentDrawingParams(
              res.data.id,
              res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
              res?.data?.pixels_per_meter_scale || 0
            )

            // Update initial items
            this.updateInitialItems(res.data.modified_items)
        },

        async restoreDeletedKopItem(kopItemId: string | number) {
            const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

            const payload = {
                is_removed: false,
                // Mark as Delete Drawing KOP Item
                is_delete_drawing_kop_item: true,
                // Changed and not saved items for data consistency
                updated_drawing_items: JSON.stringify(changedItems),
                // Unsaved drawing items to save before KOP Item delete for data consistency
                unsaved_drawing_items: JSON.stringify(unsavedItems),
            }

            // return Actual Drawing with newly restored KOP Item
            const res = await $updateKopItem(kopItemId, payload)

            // Update current items with new Drawing Items
            this.setItems(res.data.modified_items)

            // Update current Drawing params
            this.updateCurrentDrawingParams(
              res.data.id,
              res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
              res?.data?.pixels_per_meter_scale || 0
            )

            // Update initial items
            this.updateInitialItems(res.data.modified_items)
        },
        updateItemPosition(item: any) {
            const itemToUpdate = this.findItemById(item.attrs.id)
            if (item.attrs?.type === 'wf-route-name-label') {
                const currentFARouteIdx = this.funcAreaRoutes?.findIndex(i => i.id === item.attrs?.route_id)
                const currentFARoute = this.funcAreaRoutes?.[currentFARouteIdx]
                if (currentFARoute) {
                    // Handle Vue Reactivity
                    if (currentFARoute.route_name_label_config &&
                      typeof currentFARoute.route_name_label_config === 'object') {
                        currentFARoute.route_name_label_config.x = item.attrs.x
                        currentFARoute.route_name_label_config.y = item.attrs.y
                    } else {
                        currentFARoute.route_name_label_config = {
                            x: item.attrs.x,
                            y: item.attrs.y,
                        }
                    }
                }
            }
            else if (itemToUpdate && item.attrs?.type === 'kop_item_real_width_label') {
                itemToUpdate.realWidthLabelX = item.attrs.x
                itemToUpdate.realWidthLabelY = item.attrs.y
            }
            else if (itemToUpdate && item.attrs?.type === 'kop_item_code_label') {
                itemToUpdate.kopItemCodeLabelX = item.attrs.x
                itemToUpdate.kopItemCodeLabelY = item.attrs.y
            }
            else if (itemToUpdate) {
                itemToUpdate.x = item.attrs.x
                itemToUpdate.y = item.attrs.y
            }
        },
        updateOfflineItemPosition(item: any) {
            const itemToUpdate = this.findOfflineItemById(item)
            if (itemToUpdate) {
                itemToUpdate.x = item.attrs.x
                itemToUpdate.y = item.attrs.y
            }
        },
        selectBtn(btn: string) {
            this.selectedBtn = btn
        },
        selectDrawingItemById(id: string) {
            this.selectedItem = this.items.find(item => item.id === id)
            this.selectedItems = [this.items.find(item => item.id === id)]
        },
        asyncSelectDrawingItemById(id: string) {
            // Use setTimeout to ensure the DOM is updated before attempting to select the new item
            setTimeout(() => {
                // Call the method to select the new drawing item by its ID
                this.selectDrawingItemById(id)
            }, 0) // Delay of 0ms to push the function to the end of the event loop
        },
        calculateImgCenter(img: any) {
            // Set default values for scaleX and scaleY if not set
            const scaleX = img.scaleX ? img.scaleX : 1
            const scaleY = img.scaleY ? img.scaleY : 1

            const centerX = img.x + (img.width * scaleX) / 2
            const centerY = img.y + (img.height * scaleY) / 2
            const rotationInRadians = ((img?.rotation || 0) * Math.PI) / 180
            const rotatedCenterX =
                img.x + Math.cos(rotationInRadians) * (centerX - img.x) -
                Math.sin(rotationInRadians) * (centerY - img.y)
            const rotatedCenterY =
                img.y + Math.sin(rotationInRadians) * (centerX - img.x) +
                Math.cos(rotationInRadians) * (centerY - img.y)
            return [rotatedCenterX, rotatedCenterY]
        },
        setFuncAreaRoutes(funcAreaRoutes: any) {
            if (funcAreaRoutes && Array.isArray(funcAreaRoutes)) {
                this.funcAreaRoutes = funcAreaRoutes.map(route =>
                  reactive({
                      ...route,
                      is_shown: false,
                      is_shown_route_info_only: false,
                      lines: route.lines ? route.lines.map((line: any) => reactive({ ...line })) : []
                  })
                )
                this.initialFuncAreaRoutes = JSON.parse(JSON.stringify(
                  funcAreaRoutes.map(i => ({ ...i, is_shown: false, is_shown_route_info_only: false }))
                ))
            } else {
                this.funcAreaRoutes = []
                this.initialFuncAreaRoutes = []
            }
        },
        async uploadCurrentFuncAreaRoutes() {
            try {
                // Update current drawing if exist
                if (this.currentDrawingId) {
                    const payload = {
                        fa_routes: this.funcAreaRoutes
                    }
                    const res = await $updateDrawing(this.currentDrawingId, payload)

                    // Update current Drawing params
                    this.updateCurrentDrawingParams(
                      res.data.id,
                      res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
                      res?.data?.pixels_per_meter_scale || 0
                    )

                    // Update initial items
                    this.updateInitialItems(res.data.modified_items)

                    // Update funcAreaRoutes with info from updated drawing
                    this.setFuncAreaRoutes(res.data?.fa_routes)
                }
                // Else create drawing with new funcAreaRoutes
                else {
                    const res = await this.saveItems()
                    this.setItems(res?.data?.modified_items || [])
                }
            } catch (e) {
                console.log(e)
            }
        },
        updateDrawingItemsWithCurrentRouteItems (isInitialRouteItems = false) {
            const currentFARoute = isInitialRouteItems
              ? this.initialFuncAreaRoutes?.[this.selectedFARouteIdx]
              : this.funcAreaRoutes?.[this.selectedFARouteIdx]
            const currentFARouteItems = currentFARoute?.signsOrder || []

            const routeLevelTransitionItems = currentFARouteItems.filter((i: any) => i.type === 'level_transition')
            this.items = [...this.items, ...currentFARoute.lines, ...routeLevelTransitionItems]
        },
        updateRouteItemConnectedLinesPosition (itemIndex: number, newX: number, newY: number) {
            const currentFARoute = this.funcAreaRoutes?.[this.selectedFARouteIdx]

            // Update lines that connect to this item
            // The item at position N connects to:
            // - Line N-1 (if exists) as the end point (points[2], points[3])
            // - Line N (if exists) as the start point (points[0], points[1])

            // Update previous line (if exists) - update end point (last two points)
            if (itemIndex > 0 && itemIndex - 1 < currentFARoute?.lines?.length) {
                const prevLine = currentFARoute?.lines?.[itemIndex - 1]
                if (prevLine?.points?.length >= 2) {
                    prevLine.points[prevLine.points.length - 2] = newX
                    prevLine.points[prevLine.points.length - 1] = newY
                }
            }

            // Update next line (if exists) - update start point (first two points)
            if (itemIndex < currentFARoute?.signsOrder?.length - 1 && itemIndex < currentFARoute?.lines?.length) {
                const nextLine = currentFARoute.lines[itemIndex]
                if (nextLine?.points?.length >= 2) {
                    nextLine.points[0] = newX
                    nextLine.points[1] = newY
                }
            }
        },
        addNewRouteLine(color?: string) {
            const stroke = this.funcAreaRoutes[this.selectedFARouteIdx]?.color
            return reactive({
                type: 'line',
                id: uuidv4(),
                tag: 'route',
                points: [],
                stroke: stroke || color,
                strokeWidth: 3,
                lineCap: 'round',
                opacity: 1,
                dash: [10, 10],
                draggable: false,
                hitStrokeWidth: 10,
                offset: 10,
                anchor: [],
                faRouteId: this.funcAreaRoutes[this.selectedFARouteIdx].id,
                faId: this.funcAreaRoutes[this.selectedFARouteIdx].fa_id,
            })
        },

        changeRouteSignsOrder(oldSignsOrder: any[], newSignsOrder: any[]) {
            // Extract IDs
            const oldIds = oldSignsOrder.map(item => item.id)
            const newIds = newSignsOrder.map(item => item.id)

            // Create sets for quick lookup
            const newIdSet = new Set(newIds)
            const oldIdSet = new Set(oldIds)

            // Find deleted items and their indexes in oldSignsOrder
            const deletedIndexes: number[] = []
            oldSignsOrder.forEach((item, index) => {
                if (!newIdSet.has(item.id)) {
                    deletedIndexes.push(index)
                }
            })

            const wasDeleted = deletedIndexes.length > 0

            // Check if order changed for common items
            const commonOldOrder = oldSignsOrder
              .filter(item => newIdSet.has(item.id))
              .map(item => item.id)

            const commonNewOrder = newSignsOrder
              .filter(item => oldIdSet.has(item.id))
              .map(item => item.id)

            let isOrderChanged = false
            if (commonOldOrder.length > 0) {
                for (let i = 0; i < commonOldOrder.length; i++) {
                    if (commonOldOrder[i] !== commonNewOrder[i]) {
                        isOrderChanged = true
                        break
                    }
                }
            }

            const route = this.funcAreaRoutes[this.selectedFARouteIdx]

            // If order changed - rebuild entire route
            if (isOrderChanged) {
                handleFullRebuildWFRouteWithNewSignsOrder(route, newSignsOrder, this.addNewRouteLine,
                  this.calculateImgCenter, this.updateDrawingWithRouteItems)
            }
            // If only deletions without order change - remove affected lines with anchors only
            else if (wasDeleted) {
                handleWFRouteDeletions(route, oldSignsOrder, newSignsOrder, deletedIndexes, this.addNewRouteLine,
                  this.calculateImgCenter, this.updateDrawingWithRouteItems)
            }
            // If nothing changed - do nothing
        },

        // Helper func to update drawing with route items
        updateDrawingWithRouteItems() {
            this.items = this.items.filter(item => item.tag !== 'route' && item.tag !== 'anchor')
            this.updateDrawingItemsWithCurrentRouteItems()
        },

        changeRouteSettings(
          selectedFARouteIdx: number,
          selectedFARouteId: number | string | null,
          name: string,
          color: string,
          excludedSignIds: number[],
        ) {
            const route = this.funcAreaRoutes[selectedFARouteIdx]

            // Update direct properties
            route.name = name
            route.color = color
            route.excluded_sign_ids = excludedSignIds

            // Update lines with reactivity
            route.lines.forEach((line: any) => {
                // Update line color only (skip anchors)
                if (line && line.type === 'line' && line.tag === 'route') {
                    line.stroke = color
                }
            })

            this.items = this.items.map(item => item.faRouteId === selectedFARouteId && item.tag === 'route' ? {...item, stroke: color} : item)
        },
        createFARoute (funcAreaId: number, routeName: string, color: string) {
            // Hide All FA Routes before Create New Route
            this.hideAllFARoutes()

            const newRouteId = uuidv4()
            this.funcAreaRoutes.push({
                id: newRouteId,
                name: routeName,
                fa_id: funcAreaId,
                lines: [],
                color,
                signsOrder: [],
                is_shown: true,
                is_shown_route_info_only: false
            })
            this.selectedFARouteId = newRouteId

            this.selectedBtn = 'lock'
            this.isSetRouteMode = true
            const line = this.addNewRouteLine(color)
            this.funcAreaRoutes[this.selectedFARouteIdx].lines = [line]

            this.updateDrawingItemsWithCurrentRouteItems()
        },
        editFARoute (faRouteId: string | number | null) {
            if (!faRouteId) return

            // Hide All FA Routes before Edit Route
            this.hideAllFARoutes()

            this.selectedFARouteId = faRouteId

            this.selectedBtn = 'lock'
            this.isSetRouteMode = true
            this.funcAreaRoutes[this.selectedFARouteIdx].is_shown = true
            this.updateDrawingItemsWithCurrentRouteItems()
        },
        addRouteLine (e: any) {
            // ClearSelectedItems
            if (!e?.target?.attrs?.type) {
                this.clearSelectedItems()
            }

            // Add Route Line/Line Anchor only when Lock btn pressed
            if (this.selectedBtn !== 'lock') return

            const funcAreaLines = this.funcAreaRoutes[this.selectedFARouteIdx].lines.filter((item: any) => item.tag !== 'anchor')
            const targetRouteLine = funcAreaLines[funcAreaLines.length - 1]
            if (e.target?.attrs?.type === 'sign' || e.target?.attrs?.type === 'level_transition') {
                const isItemInRoute = this.funcAreaRoutes[this.selectedFARouteIdx]
                  .signsOrder?.findIndex((item: any) => item.id === e.target?.attrs?.id)
                if (isItemInRoute === -1) {
                    this.funcAreaRoutes[this.selectedFARouteIdx].signsOrder.push(e.target?.attrs)

                    if (targetRouteLine?.points?.length < 3) {

                        // Update points directly to maintain reactivity
                        const newPoints = [...(targetRouteLine?.points || []), ...this.calculateImgCenter(e.target.attrs)]
                        targetRouteLine.points = newPoints

                        // Find and update the item directly
                        const itemToUpdate = this.items.find(item => item.id === targetRouteLine.id)
                        if (itemToUpdate) {
                            itemToUpdate.points = newPoints
                        }
                    } else {
                        const line = this.addNewRouteLine()
                        const lastTwoPoints = targetRouteLine.points.slice(-2)
                        // @ts-ignore
                        line.points = [...lastTwoPoints, ...this.calculateImgCenter(e.target.attrs)]

                        this.funcAreaRoutes[this.selectedFARouteIdx].lines.push(line)
                        this.items.push(line)
                    }
                }
            }
            else if (
              e.target?.attrs?.tag === 'route' &&
              e.target?.attrs?.type === 'line'
            ) {
                const pos = e.target.getStage().getPointerPosition()
                const x = (pos.x - e.currentTarget.attrs.x) / this.scale.x
                const y = (pos.y - e.currentTarget.attrs.y) / this.scale.y
                const id = uuidv4()
                const anchor = {
                    x,
                    y,
                    radius: 6,
                    stroke: 'black',
                    fill: 'white',
                    strokeWidth: 2,
                    draggable: true,
                    type: 'circle',
                    tag: 'anchor',
                    opacity: 1,
                    id,
                    faRouteId: this.funcAreaRoutes[this.selectedFARouteIdx].id,
                    faId: this.funcAreaRoutes[this.selectedFARouteIdx].fa_id,
                }
                this.items.push(anchor)
                this.addAnchorToLine(e, x, y, id)
                this.funcAreaRoutes[this.selectedFARouteIdx].lines.push(anchor)
            }
        },
        addAnchorToLine (e: any, xAnchor: number, yAnchor: number, id: string) {
            wfRouteAddAnchorToLine(e, xAnchor, yAnchor, id, this.items)
        },
        updateLineAnchor (e: any) {
            const anchorNew = e.target.attrs
            const line = this.items.find(item => item.anchor &&
                item.anchor.some((anchorItem: any) => anchorItem.id === anchorNew.id));
            const anchor = line.anchor.find((item: any) => item.id === anchorNew.id)
            const index = line.points.findIndex((item: any) => item === anchor.x)
            line.points.splice(index, 2, anchorNew.x, anchorNew.y)
            anchor.x = anchorNew.x
            anchor.y = anchorNew.y
            this.items.find(item => item.id === line.id).points = [...line.points]
        },

        updateRouteRootLineAnchor(e: any) {
            const attrs = e.target?.attrs
            if (!attrs) return

            const anchorId = attrs.id
            const newX = attrs.x
            const newY = attrs.y
            const lineIds = Array.isArray(attrs.lineIds) ? attrs.lineIds : []

            // Updating anchor: { anchorId, newX, newY, lineIds }

            if (lineIds.length === 0) {
                // No lineIds found for anchor: anchorId
                return
            }

            // Find all connected lines
            const connectedLines = this.items.filter((line: any) =>
              lineIds.includes(line.id)
            )

            // Found connected lines: connectedLines.map(l => l.id)
            if (connectedLines.length === 0) {
                // No connected lines found for anchor: anchorId
                return
            }

            // Get the current route for line relationships
            const currentRoute = this.funcAreaRoutes?.find(route =>
              route.lines?.some((line: any) => lineIds.includes(line.id))
            )

            if (!currentRoute?.lines) {
                // No route found for anchor: anchorId
                return
            }

            const allRouteLines = currentRoute.lines.filter((item: any) => item.type === 'line' && item.tag === 'route')

            // Update each connected line
            connectedLines.forEach(line => {
                this.updateSingleLineAnchor(line, anchorId, newX, newY, allRouteLines)
            })
        },

        updateSingleLineAnchor(line: any, anchorId: string, newX: number, newY: number, allRouteLines: any[]) {
            if (!line.points || line.points.length < 4) {
                //  Line has invalid points: line.id, line.points
                return
            }

            const lineIndex = allRouteLines.findIndex((l: any) => l.id === line.id)
            if (lineIndex === -1) {
                // Line not found in route: line.id
                return
            }

            const points = [...line.points]
            const isFirstLine = lineIndex === 0
            const isLastLine = lineIndex === allRouteLines.length - 1

            // Use stored role or determine by anchor ID structure
            const anchorRole = this.determineAnchorRoleFromId(anchorId) ||
              this.getStoredAnchorRole(line, anchorId) ||
              this.determineAnchorRoleByPosition(line, newX, newY)

            if (!anchorRole) {
                // Could not determine anchor role for line: line.id, anchorId
                return
            }

            // Updating line ${line.id} ${anchorRole} point
            // Update the current line
            if (anchorRole === 'start') {
                points[0] = newX
                points[1] = newY

                // Update previous line if exists
                if (!isFirstLine) {
                    const prevLine = allRouteLines[lineIndex - 1]
                    this.updateAdjacentLine(prevLine, 'end', newX, newY)
                }
            } else { // 'end'
                const lastPointIndex = points.length - 2
                points[lastPointIndex] = newX
                points[lastPointIndex + 1] = newY

                // Update next line if exists
                if (!isLastLine) {
                    const nextLine = allRouteLines[lineIndex + 1]
                    this.updateAdjacentLine(nextLine, 'start', newX, newY)
                }
            }

            // Update the line in store
            this.updateLineInStore(line.id, points, anchorId, newX, newY, anchorRole)
        },

        // Determine role from anchor ID structure (main method)
        determineAnchorRoleFromId(anchorId: string): 'start' | 'end' | null {
            if (anchorId.includes('_start')) return 'start'
            if (anchorId.includes('_end')) return 'end'
            return null
        },

        // Get stored role from line data
        getStoredAnchorRole(line: any, anchorId: string): 'start' | 'end' | null {
            if (!Array.isArray(line.anchor)) return null
            const anchorObj = line.anchor.find((a: any) => a.id === anchorId)
            return anchorObj?.role || null
        },

        // Fallback: determine by position
        determineAnchorRoleByPosition(line: any, newX: number, newY: number): 'start' | 'end' | null {
            const points = line.points
            if (!points || points.length < 4) return null

            const startDist = Math.hypot(points[0] - newX, points[1] - newY)
            const endDist = Math.hypot(points[points.length - 2] - newX, points[points.length - 1] - newY)

            // Use larger threshold for initial determination
            const PROXIMITY_THRESHOLD = 100

            if (startDist < PROXIMITY_THRESHOLD && endDist < PROXIMITY_THRESHOLD) {
                return startDist <= endDist ? 'start' : 'end'
            } else if (startDist < PROXIMITY_THRESHOLD) {
                return 'start'
            } else if (endDist < PROXIMITY_THRESHOLD) {
                return 'end'
            }

            return null
        },

        updateAdjacentLine(line: any, role: 'start' | 'end', newX: number, newY: number) {
            if (!line.points || line.points.length < 4) return

            const lineInStore = this.items.find((item: any) => item.id === line.id)
            if (!lineInStore) return

            const points = [...lineInStore.points]

            if (role === 'start') {
                points[0] = newX
                points[1] = newY
            } else {
                const lastPointIndex = points.length - 2
                points[lastPointIndex] = newX
                points[lastPointIndex + 1] = newY
            }

            lineInStore.points = points
        },

        updateLineInStore(lineId: string, points: number[], anchorId: string, newX: number, newY: number, role: 'start' | 'end') {
            const lineInStore = this.items.find((item: any) => item.id === lineId)
            if (!lineInStore) return

            // Update points
            lineInStore.points = points

            // Update anchor data
            this.updateLineAnchorData(lineInStore, anchorId, newX, newY, role)
        },

        updateLineAnchorData(line: any, anchorId: string, newX: number, newY: number, role: 'start' | 'end') {
            if (!Array.isArray(line.anchor)) {
                line.anchor = []
            }

            let anchorObj = line.anchor.find((a: any) => a.id === anchorId)

            if (anchorObj) {
                anchorObj.x = newX
                anchorObj.y = newY
                anchorObj.role = role
            } else {
                line.anchor.push({
                    id: anchorId,
                    x: newX,
                    y: newY,
                    role: role,
                    tag: 'anchor',
                    radius: 6,
                    stroke: 'black',
                    fill: 'white',
                    draggable: true,
                })
            }
        },

        async updateZIndexFARouteLines(target: any, action: 'top' | 'bottom') {
            const layer = target.getLayer()
            if (!layer?.children?.length) return

            try {
                // Get all nodes
                const nodes = Array.from(layer.children)

                const lines = nodes.filter((node: any) =>
                  node.attrs?.type === 'line' && node.attrs?.tag === 'route'
                )
                const anchors = nodes.filter((node: any) =>
                  node.attrs?.tag === 'anchor'
                )

                // Use Konva's built-in z-index methods in the correct order
                if (action === 'top') {
                    // Move lines to top, then anchors to top (anchors will be above lines)
                    lines.forEach((line: any) => line.moveToTop())
                    await nextTick()
                    anchors.forEach((anchor: any) => anchor.moveToTop())
                } else {
                    // Move lines to bottom, then anchors to top
                    lines.forEach((line: any) => line.moveToBottom())
                    await nextTick()
                    anchors.forEach((anchor: any) => anchor.moveToTop())
                }

                await nextTick()
                layer.draw()

            } catch (error) {
                console.log('Error in updateZIndexFARouteLines:', error)
                layer.draw()
            }
        },

        hideAllFARoutes () {
            this.isSetRouteMode = false
            this.selectedFARouteId = null
            this.isShowLastSavedVersionRouteMode = false
            this.funcAreaRoutes = this.funcAreaRoutes.map(i => ({
                ...i,
                is_shown: false,
                is_shown_route_info_only: false
            }))
            this.items = this.items.filter(item => item.tag !== 'route' && item.tag !== 'anchor')

            // Update Sign Id Filters
            this.handleSignIdFilters()
        },
        async saveRoute () {
            this.funcAreaRoutes[this.selectedFARouteIdx as number].lines = this.items.filter(item =>
              (item.type === 'line' && item.tag === 'route') || item.tag === 'anchor'
            )
            // Update on server
            await this.uploadCurrentFuncAreaRoutes()

            // Refresh/update current FA Route data with new server data
            const currentFaRouteId = JSON.parse(JSON.stringify(this.selectedFARouteId))

            // Hide All FA Routes before Edit Route
            this.hideAllFARoutes()

            this.selectedFARouteId = currentFaRouteId

            this.isSetRouteMode = true
            this.funcAreaRoutes[this.selectedFARouteIdx].is_shown = true
            this.updateDrawingItemsWithCurrentRouteItems()
        },
        async deleteRoute () {
            this.funcAreaRoutes.splice(this.selectedFARouteIdx, 1)
            this.hideAllFARoutes()
            await this.uploadCurrentFuncAreaRoutes()
        },
        removeSignFromRoute (signId: any) {
            if (!signId) return

            // Get Route Signs
            const editableRoute = this.funcAreaRoutes[this.selectedFARouteIdx]
            const orderedSigns = editableRoute?.signsOrder || []

            // Check if the sign is currently in the route
            if (orderedSigns.find((i: any) => signId === i?.sign)) {
                // Remove selected sign
                const filteredOrderedSigns = orderedSigns.filter((i: any) => signId !== i?.sign)
                // Update Route Order
                this.changeRouteSignsOrder(orderedSigns, filteredOrderedSigns)
            }

            // Exclude selected sign
            this.funcAreaRoutes[this.selectedFARouteIdx].excluded_sign_ids =
              [ ...(editableRoute?.excluded_sign_ids || []), signId ]
        },
        showSignRoute (faRouteId: string, isShowRouteInfoOnly: boolean = false) {
            // Hide all routes if another view mode was activated
            if (isShowRouteInfoOnly) {
                if (this.funcAreaRoutes.filter(route => route.is_shown === true).length) {
                    this.hideAllFARoutes()
                }
            } else {
                if (this.funcAreaRoutes.filter(route => route.is_shown_route_info_only === true).length) {
                    this.hideAllFARoutes()
                }
            }

            const faRouteIdx = this.funcAreaRoutes.findIndex((i) => i?.id === faRouteId)
            const selectedFARoute = this.funcAreaRoutes[faRouteIdx]

            if (selectedFARoute) {
                const selectedFARouteItems = selectedFARoute?.signsOrder || []
                const routeLevelTransitionItems = selectedFARouteItems.filter((i: any) => i.type === 'level_transition')

                if (isShowRouteInfoOnly) {
                    selectedFARoute.is_shown_route_info_only = true
                } else {
                    selectedFARoute.is_shown = true
                }

                this.items = [
                  ...this.items,
                  // show FA Routes without anchors
                  // i.e. without ability to change routes because not is SetRouteMode, only View
                  ...selectedFARoute.lines.filter((i: any) => !(i.tag === 'anchor')),
                  // show also Level Transition Item
                  ...routeLevelTransitionItems
                ]

                // Update Sign Id Filters
                this.handleSignIdFilters()
            }
        },
        hideSignRoute (faRouteId: string, isShowRouteInfoOnly: boolean = false) {
            // Hide all routes if another view mode was activated
            if (isShowRouteInfoOnly) {
                if (this.funcAreaRoutes.filter(route => route.is_shown === true).length) {
                    this.hideAllFARoutes()
                }
            } else {
                if (this.funcAreaRoutes.filter(route => route.is_shown_route_info_only === true).length) {
                    this.hideAllFARoutes()
                }
            }

            const faRouteIdx = this.funcAreaRoutes.findIndex((i) => i?.id === faRouteId)
            const selectedFARoute = this.funcAreaRoutes[faRouteIdx]

            if (selectedFARoute) {
                const selectedFARouteItems = selectedFARoute?.signsOrder || []
                const routeLevelTransitionItems = selectedFARouteItems.filter((i: any) => i.type === 'level_transition')

                if (isShowRouteInfoOnly) {
                    selectedFARoute.is_shown_route_info_only = false
                } else {
                    selectedFARoute.is_shown = false
                }

                const selectedFARouteLineIds = selectedFARoute.lines.map((item: any) => item.id)
                const routeLevelTransitionIds = routeLevelTransitionItems.map((item: any) => item.id)
                const selectedFARouteItemIds = [...selectedFARouteLineIds, ...routeLevelTransitionIds]

                this.items = this.items.filter(item => !selectedFARouteItemIds.includes(item.id))

                // Update Sign Id Filters
                this.handleSignIdFilters()
            }
        },
        showAllSignRoutes(isShowRouteInfoOnly: boolean = false) {
            if (isShowRouteInfoOnly) {
                // Mark all FA routes as shown_route_info_only
                this.funcAreaRoutes = this.funcAreaRoutes.map(route => ({
                    ...route,
                    is_shown: false,
                    is_shown_route_info_only: true
                }))
            } else {
                // Mark all FA routes as shown
                this.funcAreaRoutes = this.funcAreaRoutes.map(route => ({
                    ...route,
                    is_shown: true,
                    is_shown_route_info_only: false
                }))
            }

            this.showSelectedSignFaRoutes(this.funcAreaRoutes)
        },

        updateSignRoutesVisibility() {
            // Get all shown FA routes
            const funcAreaRoutes = this.funcAreaRoutes.filter(route => route.is_shown)
            this.showSelectedSignFaRoutes(funcAreaRoutes)
        },

        showSelectedSignFaRoutes(funcAreaRoutes: any[]) {
            // flatten and collect all items
            const allLines = funcAreaRoutes.flatMap(route =>
              route.lines.filter((i: any) => i.tag !== 'anchor') // skip anchors
            )

            const allLevelTransitions = funcAreaRoutes.flatMap(route =>
              (route?.signsOrder || []).filter((i: any) => i.type === 'level_transition')
            )

            this.items = [
                ...this.items,
                ...allLines,
                ...allLevelTransitions
            ]

            // Update Sign Id Filters
            this.handleSignIdFilters()
        },

        handleSignIdFilters() {
            const shownFARoutes = this.funcAreaRoutes.filter(route => route.is_shown_route_info_only === true)

            const signIds = shownFARoutes.flatMap(route =>
              route?.signsOrder
                ?.filter((item: any) => item.type === 'sign' && !!item.sign)
                ?.map((item: any) => item.sign) || []
            )

            this.selectedSignIdsFilter = Array.from(new Set(signIds))
        },
        selectSignType(signType: object) {
            this.selectedSignType = signType
            this.selectedBtn = 'sign'
        },
        selectKopItemType(kopItemType: object) {
            this.selectedKopItemType = kopItemType
            this.selectedBtn = 'kop'
        },
        updateGeoMapKey() {
            this.geoMapKey = this.geoMapKey + 1
        },
        addGeoPoints(geoData: any[]) {
            if (geoData) {
                this.geoPoints = [...geoData]
            } else {
                this.geoPoints = []
            }
        },
        async uploadCurrentGeoPoints() {
            try {
                // Update current drawing if exist
                if (this.currentDrawingId) {
                    const payload = {
                        geo_points: this.geoPoints
                    }
                    const res = await $updateDrawing(this.currentDrawingId, payload)

                    // Update current Drawing params
                    this.updateCurrentDrawingParams(
                      res.data.id,
                      res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
                      res?.data?.pixels_per_meter_scale || 0
                    )

                    // Update initial items
                    this.updateInitialItems(res.data.modified_items)
                }
                // Else create drawing with new geoPoints
                else {
                    const res = await this.saveItems()
                    this.setItems(res?.data?.modified_items || [])
                }
            } catch (e) {
                console.log(e)
            }
        },
        showGeoMap() {
            // If switching not from GeoDrawingMode - calculateMapCenter
            if (!this.isGeoDrawingMode) {
                this.calculateMapCenter()
            }
            this.createGeoPoints()
            this.isGeoDrawingMode = false
            this.isGeoSetMode = false
            this.isDrawingScaleSetMode = false
            this.isGeoMapMode = true
        },
        showGeoSetMode() {
            this.isGeoMapMode = false
            this.isGeoDrawingMode = false
            this.isDrawingScaleSetMode = false
            this.isGeoSetMode = !this.isGeoSetMode
        },
        showGeoSetScaleMode() {
            this.isGeoMapMode = false
            this.isGeoDrawingMode = false
            this.isGeoSetMode = false
            this.isDrawingScaleSetMode = true

            // Clear selected Items
            this.clearSelectedItems()

            // Safely handle selectedBtn parsing
            let selectedBtnOldValue = 'move' // default value
            try {
                selectedBtnOldValue = this.selectedBtn ? JSON.parse(JSON.stringify(this.selectedBtn)) : 'move'
            } catch (e) {
                selectedBtnOldValue = 'move'
            }

            this.selectBtn('move')
            if (selectedBtnOldValue !== 'move') {
                this.lockMoving()
            }

            // Safely handle tempItems copy
            try {
                // Deep copy
                this.tempItems = this.items ? JSON.parse(JSON.stringify(this.items)) : []
            } catch (e) {
                this.tempItems = []
            }

            this.items = !!this.currentDrawingSetScaleLineConfig ? [{...this.currentDrawingSetScaleLineConfig}] : [
                {
                    id: 'set-scale-measure-line',
                    type: 'line',
                    points: [0, 0, (this.bgImg?.width || 300) / 3, 0],
                    stroke: '#DD082B',
                    fill: '#DD082B',
                    strokeWidth: 2,
                    hitStrokeWidth: 10,
                    lineCap: 'round',
                    opacity: 1,
                    draggable: true,
                    scaleX: 1,
                    scaleY: 1,
                    x: (this.bgImg?.width || 300) / 3 ,
                    y: (this.bgImg?.height || 300) / 2
                }
            ]

            this.setScaleModeLineSize = parseFloat(this.currentDrawingSetScaleLineConfig?.set_scale_line_size) || 1
        },
        showGeoDrawingMode() {
            // If switching not from GeoMapMode - calculateMapCenter
            if (!this.isGeoMapMode) {
                this.calculateMapCenter()
            }
            this.createGeoPoints()
            this.isGeoMapMode = false
            this.isGeoSetMode = false
            this.isDrawingScaleSetMode = false
            this.isGeoDrawingMode = true
        },
        hideGeoDrawingMode() {
            this.isGeoDrawingMode = false
            this.setZoom(this.initialZoom || 1)
        },
        hideGeoMapMode() {
            this.isGeoMapMode = false
            this.setZoom(this.initialZoom || 1)
        },
        hideSetScaleMode(isRestoreItems: boolean = true) {
            this.isDrawingScaleSetMode = false
            if (isRestoreItems) {
                this.items = JSON.parse(JSON.stringify(this.tempItems))  // deep copy
            }

            // Clear selected Items
            this.clearSelectedItems()
        },
        async setMapPixelsPerMeterScale (pixelsPerMeter: number, setScaleLineConfigStr: string) {
            if (!pixelsPerMeter) return

            const updatedPixelsPerMeter = parseFloat(pixelsPerMeter?.toFixed(5) || '0')

            try {
                // Update current drawing if exist
                if (this.currentDrawingId) {
                    const payload = {
                        set_scale_line_config: setScaleLineConfigStr,
                        pixels_per_meter_scale: updatedPixelsPerMeter
                    }
                    const res = await $updateDrawing(this.currentDrawingId, payload)
                    this.setItems(res?.data?.modified_items || [])

                    // Update current Drawing params
                    this.updateCurrentDrawingParams(
                      res.data.id,
                      res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
                      res?.data?.pixels_per_meter_scale || 0
                    )

                    // Update initial items
                    this.updateInitialItems(res.data.modified_items)
                }
                // Else create drawing with new geoPoints
                else {
                    const res = await this.saveItems(
                      [],
                      {setScaleLineConfig: setScaleLineConfigStr, pixelsPerMeterScale: updatedPixelsPerMeter}
                    )
                    this.setItems(res?.data?.modified_items || [])
                }
            } catch (e) {
                console.log(e)
            }
        },

        /**
         * Calculates the center of the Geo Map based on geoPoints.
         * If this.mapCenter is empty, has a length different from 2, or is [0, 0], it recalculates the center.
         * If this.mapCenter has a length of 2 and is not [0, 0], it does nothing.
         */
        calculateMapCenter() {
            let sumLat = 0
            let sumLon = 0

            // Sum all latitudes and longitudes from geoPoints
            this.geoPoints.forEach(point => {
                sumLat += point.lat
                sumLon += point.lon
            })

            // Calculate the average latitude and longitude
            const avgLat = sumLat / this.geoPoints.length
            const avgLon = sumLon / this.geoPoints.length

            // Set the mapCenter to the calculated average values
            this.mapCenter = [avgLon, avgLat]
        },

        createGeoPoints() {
            updateItemsLonLat(this.geoPoints, this.items)

            const pdfWidth = this.width / this.scale.x
            const pdfHeight = this.height / this.scale.y
            const pdfCorners = [{x: 0, y:0}, {x: pdfWidth, y:0}, {x: pdfWidth, y: pdfHeight}, {x: 0, y: pdfHeight}]
            updateItemsLonLat(this.geoPoints, pdfCorners)

            this.pdfCornersGeo = pdfCorners

            const newInitialItems = this.initialItems.map((initialItem: any) => {
                // Find matching item in current items by ID
                const matchingItem = this.items.find((item: any) => item.id && item.id === initialItem.id)

                if (matchingItem) {
                    // Update only lat/lon if they exist in matchingItem
                    return {
                        ...initialItem,
                        lat: matchingItem.lat !== undefined ? matchingItem.lat : initialItem.lat,
                        lon: matchingItem.lon !== undefined ? matchingItem.lon : initialItem.lon
                    }
                }

                // No matching item found - return original
                return initialItem
            })

            this.updateInitialItems(newInitialItems)
        },

        addPolygon(e: any, x: number, y: number) {
            const pos = e.target.getStage().getPointerPosition()
            // If Initial Polygon Anchor - pass
            if (e.target.attrs.id !== 'initial-polygon-anchor') {
                this.addPolygonPoint({ x: x, y: y })
            }
        },

        async addItem(e: any) {
            const pos = e.target.getStage().getPointerPosition()

            const initialX = (pos.x - e.currentTarget.attrs.x) / this.scale.x
            const initialY = (pos.y - e.currentTarget.attrs.y) / this.scale.y

            const pdfWidth = this.width / this.scale.x
            const pdfHeight = this.height / this.scale.y
            const x = initialX < 0 ? undefined : (initialX > pdfWidth ? undefined : initialX)
            const y = initialY < 0 ? undefined : (initialY > pdfHeight ? undefined : initialY)

            if (!x || !y) {
                useSnackbarStore().showMessage({
                    color: 'warning',
                    icon: 'mdi-alert-circle',
                    title: 'Warning',
                    text: `Please add items only within the available area of the map, not outside.`
                })
                return
            }

            if (this.selectedBtn === 'sign') {
                await this.addSign(e)
                this.selectBtn('move')
            }
            else if (this.selectedBtn === 'kop') {
                if (!this.selectedKopItemType) return

                await this.addKopItem(e)
            }
            else if (this.selectedBtn === 'polygon') {
                this.addPolygon(e, x, y)
            }
            else if (this.selectedBtn === 'route-level-transition') {
                if (!this.isSetRouteMode) return

                this.addRouteLevelTransition(e)
                this.selectBtn('lock')
            }
            else {
                const drawingItem = addDrawingItem(
                  this.selectedBtn,
                  x,
                  y,
                  this.isEditKopItemTypeShape,
                  this.initialZoom || 1
                )

                // Skip processing if no drawingItem is available
                if (!drawingItem) return

                this.items.push(drawingItem)
                this.drawingItem = drawingItem

                if (this.offlineMode) {
                    const location = this.selectedLocation ? this.selectedLocation.id : this.project.location

                    this.offlineItems.push({ ...drawingItem, location: location })
                }

                // Check if the selected button is 'text' and the new drawing item has a valid ID
                if (this.selectedBtn === 'text' && drawingItem?.id) {
                    // Select newly added Text Item by its ID
                    this.asyncSelectDrawingItemById(drawingItem.id)
                    // If Wayfinding project map view
                    if (!!this.project?.id && this.project?.type === 1) {
                        this.selectBtn('move')
                    }
                }
            }
        },

        async addKopItem(e: any) {
            if (!this.selectedKopItemType) return

            // kopItemTypeForm: 1 - Line, 2 - Individual Piece
            const kopItemTypeForm = this.selectedKopItemType?.form_type || 1

            const id = uuidv4()
            const label_id = uuidv4()
            const pos = e.target.getStage().getPointerPosition()
            const x = (pos.x - e.currentTarget.attrs.x) / this.scale.x -
              ((this.selectedKopItemType?.shape_width || 25) / 2)
            const y = (pos.y - e.currentTarget.attrs.y) / this.scale.y -
              ((this.selectedKopItemType?.shape_height || 25) / 2)

            const location = this.selectedLocation ? this.selectedLocation.id : this.project.location

            const kopItemDrawingData = await kopItemData(id, x, y, this.selectedKopItemType)

            const kopItem = {
                ...kopItemDrawingData,
                campaign: this.project.id,
                location: location,
                kop_item_type: this.selectedKopItemType.id,
            }
            this.items.push(kopItem)

            // Add labels on create if needed
            // const label = labelData(
            //   label_id,
            //   x + (this.selectedKopItemType.shape_width / 2),
            //   y + this.selectedKopItemType.shape_height, id)
            // this.items.push(label)

            if (kopItemTypeForm === 2) {
                // await this.createKopItem(id, x, y, {
                //     ...kopItemDrawingData,
                //     scaleX: 1,
                //     scaleY: 1
                // })
                this.pendingKopItems.push(kopItem)
            } else {
                this.drawingItem = kopItem
            }

            this.isKopItemCreationMode = true
        },

        async createKopItem(id: string, x: number, y: number, drawingKopItemData: any) {
            // kopItemTypeForm: 1 - Line, 2 - Individual Piece
            const kopItemTypeForm = this.selectedKopItemType?.form_type || 1

            if (kopItemTypeForm === 1) {
                // TODO: KOP: remove if no need to create one piece Distance type KOP Items
                // if (!x || !y ||
                //   !Array.isArray(drawingKopItemData?.points) ||
                //   drawingKopItemData.points.length !== 8 ||
                //   !drawingKopItemData.points.every((point: any) => typeof point === 'number' && !isNaN(point))
                // ) {
                //     useSnackbarStore().showMessage({
                //         color: 'warning',
                //         icon: 'mdi-alert-circle',
                //         title: 'Drawing KOP Item',
                //         text: 'Please drag longer to create a valid KOP item. The shape requires precise coordinates.'
                //     })
                //
                //     // Remove newly added KOP Item and KOP Item label (if exist) when error occurred
                //     this.items = this.items.filter((item: any) => {
                //         return id !== item?.id && !(item?.type === 'label' && id === item?.parent)
                //     })
                //
                //     return
                // }
            }

            try {
                const location = this.selectedLocation ? this.selectedLocation.id : this.project.location

                const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

                const kopItemPayload: KopItemCreatePayload = {
                    campaign: this.project.id,
                    location: location,
                    kop_item_type: this.selectedKopItemType.id,
                    is_removed: false,
                    top: y,
                    left: x,
                    // Drawing KOP Item Data to create item + create new drawing with newly added item and return
                    drawing_kop_item_data: JSON.stringify(drawingKopItemData),
                    // Changed and not saved items for data consistency
                    updated_drawing_items: JSON.stringify(changedItems),
                    // Unsaved drawing items to save before KOP Item Create for data consistency
                    unsaved_drawing_items: JSON.stringify(unsavedItems)

                    // TODO: Future updates - Update FA on create Kop Item with selected FA
                    // functional_area: this.selectedFA.id,
                }

                // Create a FormData object
                const formData = new FormData()

                // Append all kopItemPayload fields to FormData
                for (const key in kopItemPayload) {
                    if (Object.prototype.hasOwnProperty.call(kopItemPayload, key)) {
                        formData.append(key, kopItemPayload[key])
                    }
                }

                // return Actual Drawing with newly added KOP Item
                const res = await $createKopItem(formData)

                // Update current items with new Drawing Items
                this.setItems(res.data.modified_items)

                // Update current Drawing params
                this.updateCurrentDrawingParams(
                  res.data.id,
                  res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
                  res?.data?.pixels_per_meter_scale || 0
                )

                // Update initial items
                this.updateInitialItems(res.data.modified_items)
            } catch (e: any) {
                // Log any errors that occur during the request
                console.log(e)
                const mes = e?.response?.data ?
                  (e.response.data.detail ? e.response.data.detail : JSON.stringify(e.response.data)) :
                  e.message
                useSnackbarStore().showMessage({
                    color: 'error',
                    icon: 'mdi-alert-circle',
                    title: 'Error',
                    text: `Oops, something went wrong. ${mes}`
                })

                // Remove newly added KOP Item and KOP Item label (if exist) when error occurred
                this.items = this.items.filter((item: any) => {
                    return id !== item?.id && !(item?.type === 'label' && id === item?.parent)
                })
            }
        },

        addRouteLevelTransition (e: any) {
            const id = uuidv4()
            const pos = e.target.getStage().getPointerPosition()
            const x = (pos.x - e.currentTarget.attrs.x) / this.scale.x - 12
            const y = (pos.y - e.currentTarget.attrs.y) / this.scale.y - 12

            const routeLevelTransition = {
                type: 'level_transition',
                tag: 'route',
                id: id,
                x: x,
                y: y,
                height: 24,
                width: 24,
                opacity: 1,
                draggable: true,
                faRouteId: this.funcAreaRoutes[this.selectedFARouteIdx].id,
                faId: this.funcAreaRoutes[this.selectedFARouteIdx].fa_id,
            }

            this.items.push(routeLevelTransition)
        },

        async addSign(e: any) {
            if (!this.selectedSignType) return
            const id = uuidv4()
            const label_id = uuidv4()
            const pos = e.target.getStage().getPointerPosition()
            const x = (pos.x - e.currentTarget.attrs.x) / this.scale.x - 12
            const y = (pos.y - e.currentTarget.attrs.y) / this.scale.y - 12

            const { width, height } = await loadImage(this.selectedSignType.source)
            const ratio = width / height

            const location = this.selectedLocation ? this.selectedLocation.id : this.project.location
            const signName = this.createSignName(this.selectedSignType.trigram, this.selectedSignType.id)
            const offlineSignData = {
                ...signData(id, x, y, this.selectedSignType, 25/ratio), campaign: this.project.id,
                location: location,
                sign_type: this.selectedSignType.id,
                is_removed: false,
                top: y,
                left: x,
                sign_additional_functional_areas: [],
                sign_qty: 1,
                sign_type_colour: this.selectedSignType.colour,
                is_custom: this.selectedSignType.is_custom,
                sign_short_code: signName,
                sign_short_name: signName,
                orientation: 0,
                sign_status_name: 'Draft Dot Maps',
                notes: '',
                client_notes: '',
                fabric_notes: '',
                instruction: '',
                sign: id,
                responsible_name: '',
                assigned_to_name: '',
                is_can_user_approve: false,
                is_released: false,
            }

            // Add the new item to the drawing
            this.items.push(offlineSignData)
            const offlineLabelData = labelData(label_id, x + 10, y + 25, id)
            this.items.push(offlineLabelData)

            if (!this.offlineMode) {
                // Create sign
                await this.createSign(id, x, y, x + 10, y + 25, offlineSignData)
            }
            else {
                this.offlineSigns.push(offlineSignData)
                this.offlineSignLabels.push(offlineLabelData)
            }
        },

        createSignName(trigram: string, signId: number) {
            const maxCount = Math.max(...this.items
              .filter(item => item?.type === 'sign')?.map(item => item.sign_short_code?.split('-')[1]), 0) + 1
            const formattedMaxCount = String(maxCount).padStart(4, '0')
            return `${trigram}-${formattedMaxCount}`
        },

        async createSign(id: string, x: number, y: number, labelX: number, labelY: number, drawingSignData: any) {
            try {
                const location = this.selectedLocation ? this.selectedLocation.id : this.project.location

                const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

                const signPayload: SignCreatePayload = {
                    campaign: this.project.id,
                    location: location,
                    type: this.selectedSignType.id,
                    is_removed: false,
                    top: y,
                    left: x,
                    label_top: labelY,
                    label_left: labelX,
                    // Drawing Sign Data to create sign + create new drawing with newly added sign and return
                    drawing_sign_data: JSON.stringify({
                        width: drawingSignData.width || 25,
                        height: drawingSignData.height || 25,
                    }),
                    // Changed and not saved items for data consistency
                    updated_drawing_items: JSON.stringify(changedItems),
                    // Unsaved drawing items to save before Sign Create for data consistency
                    unsaved_drawing_items: JSON.stringify(unsavedItems)
                }

                if (!this.selectedSignType.is_custom && this.selectedSignType?.width && this.selectedSignType?.height) {
                    signPayload.width = this.selectedSignType?.width
                    signPayload.height = this.selectedSignType?.height
                }

                // Create a FormData object
                const formData = new FormData()

                // Append all signPayload fields to FormData
                for (const key in signPayload) {
                    if (Object.prototype.hasOwnProperty.call(signPayload, key)) {
                        formData.append(key, signPayload[key])
                    }
                }

                // return Actual Drawing with newly added Sign
                const res = await $createSign(formData)

                // Update current items with new Drawing Items
                this.setItems(res.data.modified_items)

                // Update current Drawing params
                this.updateCurrentDrawingParams(
                  res.data.id,
                  res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
                  res?.data?.pixels_per_meter_scale || 0
                )

                // Update initial items
                this.updateInitialItems(res.data.modified_items)

                // Update Newly Added Sign Map Snapshot
                let newlyAddedDrawingSign = null

                // Iterate from the end of the array
                for (let i = this.items.length - 1; i >= 0; i--) {
                    if (this.items[i].type === 'sign') {
                        newlyAddedDrawingSign = this.items[i]
                        break // Stop iteration as soon as we find the last 'sign'
                    }
                }
                if (!newlyAddedDrawingSign) return

                await this.updateSignMapSnapshot({ ...newlyAddedDrawingSign }, newlyAddedDrawingSign.sign)
            } catch (e: any) {
                // Log any errors that occur during the request
                console.log(e)
                const mes = e?.response?.data ?
                  (e.response.data.detail ? e.response.data.detail : JSON.stringify(e.response.data)) :
                  e.message
                useSnackbarStore().showMessage({
                    color: 'error',
                    icon: 'mdi-alert-circle',
                    title: 'Error',
                    text: `Oops, something went wrong. ${mes}`
                })

                // Remove newly added sign and sign label when error occurred
                this.items = this.items.filter(item => id !== item.id && !(item?.type === 'label' && id === item.parent))
            }
        },

        async updateSignMapSnapshot (
          drawingSign: any,
          signId: number,
          customMapSnapshot = null as Blob | null
        ) {
            if (!drawingSign || !signId) return

            const stage = this.stage.getStage()
            if (!stage) return

            await waitForKonvaImagesToLoad(stage, () => this.mapLoading)

            // Create a FormData object
            const formData = new FormData()

            let mapSnapshot = null
            if (customMapSnapshot) {
                mapSnapshot = customMapSnapshot
            } else {
                // Generate the mapSnapshot as a Blob
                mapSnapshot = await getMapSnapshot({
                    selectedItem: drawingSign, // drawing item object with: id(drawing), x, y, width, height, scaleX, scaleY
                    returnBlob: true,
                })
            }

            // Append the mapSnapshot Blob to FormData
            if (mapSnapshot && mapSnapshot instanceof Blob) {
                formData.append('map_snapshot_file', mapSnapshot, `map_snapshot_id${signId}_${getCurrentDateTimeStr()}.png`)
            }

            try {
                await $updateSign(signId, formData, { headers: { 'Content-Type': 'multipart/form-data' } })
            } catch (e) {
                console.log(e)
            }
        },

        async saveSignMapSnapshots(drawingItems: any) {
            const signItemsToProcess = drawingItems.filter((item: any) =>
              item?.type === 'sign' && !!item?.sign
            )

            if (!signItemsToProcess.length) return

            const formData = new FormData()
            const signIds: number[] = []

            // Generate all snapshots
            for (let i = 0; i < signItemsToProcess.length; i++) {
                const signItem = signItemsToProcess[i]

                const snapshot = await getMapSnapshot({
                    selectedItem: signItem,
                    returnBlob: true,
                })

                if (!(snapshot instanceof Blob)) continue

                const signId = signItem.sign
                signIds.push(signId)

                formData.append(
                  `map_snapshot_${signId}`,
                  snapshot,
                  `map_snapshot_${signId}_${getCurrentDateTimeStr()}.png`
                )
            }

            if (!signIds.length) return

            // Append IDs
            signIds.forEach(id => {
                formData.append('ids[]', String(id))
            })

            // Update request
            try {
                await $multipleUpdateSignMapSnapshots(formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                })
            } catch (e) {
                console.log(
                  'multipleUpdateSignMapSnapshots upload failed (if timed out on client, ' +
                  'server may still be processing the request):', e)
            }
        },

        async updateProjectCurrentLevelSignMapSnapshots() {
            this.updateAllSignMapSnapshotsLoading = true

            await this.saveSignMapSnapshots(this.items)

            this.updateAllSignMapSnapshotsLoading = false
        },

        async updateProjectAllLevelsSignMapSnapshots() {
            this.updateAllSignMapSnapshotsLoading = true

            const getFilteredLocationList = () => {
                if (this?.locations) {
                    // Main location + 1st level
                    const locationList = [this.locations, ...(this.locations?.children || [])]
                    // Return only locations with modified_image file (Map file)
                    return locationList.filter(loc => !!loc.modified_image)
                }
                return []
            }

            const timeout = (ms: number) => {
                return new Promise(resolve => setTimeout(resolve, ms))
            }

            const locationList = getFilteredLocationList()
            const stage = this.stage.getStage()
            const currentScale = { ...stage.scale() }
            const currentPosition = { x: stage.x(), y: stage.y() }
            const currentSelectedLocation = this.selectedLocation ? { ...this.selectedLocation } : null

            // Process each level location to generate Map Snapshots
            for (const [index, location] of locationList.entries()) {
                try {
                    this.updateLocationSetupLoading(true)
                    this.selectedLocation = location

                    // Load location data
                    this.updateLoadDrawingMapImgTrigger(1)
                    await timeout(50)
                    this.updateLoadDrawingMapImgTrigger(0)
                    await this.getDrawing(false, true)
                    await nextTick()
                    await waitForKonvaImagesToLoad(stage, () => this.mapLoading)

                    // Save Current Level Sign Map Snapshots
                    await this.saveSignMapSnapshots(this.items)
                } catch (error) {
                    console.log(`Error processing location ${location.name}:`, error)
                } finally {
                    this.updateLocationSetupLoading(false)
                }
            }

            // Restore original state
            this.selectedLocation = currentSelectedLocation
            await this.getDrawing(false, true)
            await nextTick()

            stage.scale(currentScale)
            stage.position(currentPosition)

            this.updateAllSignMapSnapshotsLoading = false
        },

        copySign() {
            let signId = null

            // Check if exactly one sign item is selected
            if (this.selectedItems?.length === 1 && this.selectedItems[0]?.type === 'sign') {
                signId = this.selectedItems[0]?.sign || null
            }

            this.copiedSignId = signId

            useSnackbarStore().showMessage({
                color: 'success',
                icon: 'mdi-checkbox-marked-circle',
                title: 'Success',
                text: 'Sign has been copied successfully!',
                timeout: 1000
            })
        },

        async duplicateSign(signIdToCopy: any = null) {
            this.duplicateLoading = true
            try {
                let signId: any = null

                if (signIdToCopy) {
                    signId = signIdToCopy
                }
                // Check if exactly one sign item is selected
                else if (this.selectedItems?.length === 1 && this.selectedItems[0]?.type === 'sign') {
                    signId = this.selectedItems[0]?.sign
                }

                const isSelectedSignExistInDrawing = this.items.find(i => i?.type === 'sign' && i?.sign === signId)

                if (!signId || !isSelectedSignExistInDrawing) {
                    useSnackbarStore().showMessage({
                        color: 'error',
                        icon: 'mdi-alert-circle',
                        title: 'Error',
                        text: 'Oops, something went wrong. The sign is not selected or does not exist'
                    })
                    return
                }

                this.signIdsToCopy.push(signId)

                if (this.signIdsToCopy.length === 0) {
                    useSnackbarStore().showMessage({
                        color: 'error',
                        icon: 'mdi-alert-circle',
                        title: 'Error',
                        text: 'Oops, something went wrong. The sign is not selected or does not exist'
                    })
                    return
                }

                const { newlyCreatedItems: unsavedItems, changedItems } = this.getNewAndChangedItems()

                const payload: any = {
                    sign_ids: this.signIdsToCopy, // Send the array of sign IDs
                    // Changed and not saved items for data consistency
                    updated_drawing_items: JSON.stringify(changedItems),
                    // Unsaved drawing items to save before Sign Create for data consistency
                    unsaved_drawing_items: JSON.stringify(unsavedItems)
                }

                // return Actual Drawing with newly added Sign
                const res = await $duplicateDrawingSign(payload)

                // Clear the array
                this.signIdsToCopy = []

                // Update current items with new Drawing Items
                this.setItems(res.data.modified_items)

                // Update current Drawing params
                this.updateCurrentDrawingParams(
                  res.data.id,
                  res?.data?.set_scale_line_config ? JSON.parse(res?.data?.set_scale_line_config) : null,
                  res?.data?.pixels_per_meter_scale || 0
                )

                // Update initial items
                this.updateInitialItems(res.data.modified_items)

                await nextTick()
                // Select last newly created/copied/duplicated sign
                this.selectLastSignItem()

                this.duplicateLoading = false
            } catch (e: any) {
                if (e?.response?.data?.error !== 'Request cancelled due to a newer request') {
                    // Log any errors that occur during the request
                    console.log(e)
                    const mes = e?.response?.data ?
                      (e.response.data.detail ? e.response.data.detail : JSON.stringify(e.response.data)) :
                      e.message
                    useSnackbarStore().showMessage({
                        color: 'error',
                        icon: 'mdi-alert-circle',
                        title: 'Error',
                        text: `Oops, ${e.name} something went wrong. ${mes}`
                    })

                    // Clear the array
                    this.signIdsToCopy = []
                    this.duplicateLoading = false
                }
            }
        },

        async getAllDrawingItems() {
            try {
                const res = await $getCampaignLastDrawings(this.project.id)
                this.allDefaultDrawingItems = transformDrawingsArrayToObj(res.data)
            } catch (e) {
                console.log(e)
            }
        },

        // Function to revoke all object URLs
        revokeAllOfflineModeObjectUrls() {
            this.offlineModeObjectUrls.forEach(url => URL.revokeObjectURL(url))
            this.offlineModeObjectUrls.length = 0
        },

        clearOfflineItems() {
            // Filter out items that are present in offlineSigns/offlineItems based on id or parent id
            this.items = this.items.filter(item =>
              !this.offlineSigns.some(i => i.id === item.id || i.id === item.parent) &&
              !this.offlineItems.some(i => i.id === item.id)
            )

            // Find items with a 'file' property that starts with 'blob:' indicating a blob URL
            const itemsWithBlob = this.items.filter(i => i.file && i.file.startsWith('blob:'))
            // Filter out items that are present in itemsWithBlob based on id or parent id
            this.items = this.items.filter(item =>
              !itemsWithBlob.some(i => i.id === item.id || i.id === item.parent)
            )

            // Clear the offlineSigns and offlineItems array(s)
            this.offlineItems = []
            this.offlineSigns = []
            this.offlineSignLabels = []
        },

        // Update selectedKopItemType to match an item in kopItemTypes by ID, or set to null if not found
        updateSelectedKopItemType() {
            if (this.selectedKopItemType?.id) {
                // Find the kopItemType in kopItemTypes array with the same ID as selectedKopItemType
                // If found, update selectedKopItemType, otherwise set to null
                this.selectedKopItemType = this.kopItemTypes
                  .find((i: any) => i.id === this.selectedKopItemType?.id) || null
            }
        },

        // Update selectedSignType to match an item in signTypes by ID, or set to null if not found
        updateSelectedSignType() {
            if (this.selectedSignType?.id) {
                // Find the signType in signTypes array with the same ID as selectedSignType
                // If found, update selectedSignType, otherwise set to null
                this.selectedSignType = this.signTypes.find((i: any) => i.id === this.selectedSignType?.id) || null
            }
        },

        // Update selectedLocation to match an item in locations by ID, or set to null if not found
        updateSelectedLocation() {
            // Check if selectedLocation has a valid id
            if (this.selectedLocation?.id) {
                // Find the location in locations array with the same ID as selectedLocation
                // If found, update selectedLocation with the found location, otherwise set to null
                this.selectedLocation = getLocationById(this.locations, this.selectedLocation?.id)
            }
            // Else - select main location
            else {
                this.selectedLocation = JSON.parse(JSON.stringify(this.locations)) // deep copy
            }
        },

        async turnOnOfflineMode() {
            saveDefaultSignTypesToLocalStorage(this.signTypes)
            saveDefaultLocationsToLocalStorage(this.locations)

            // Fetch and store images for sign types
            this.signTypes = await getSignTypesWithImg(this.signTypes)
            // Fetch and store images recursively for locations
            this.locations = await getUpdatedLocationImageUrlRecursively(this.locations)

            // Update selectedSignType with new signTypes(with base64Img)
            this.updateSelectedSignType()

            // Uncomment if needed
            // // Update selectedKopItemType with new kopItemTypes(with base64Img)
            // this.updateSelectedKopItemType()

            // Update selectedLocation with new locations(with base64Img)
            this.updateSelectedLocation()

            await this.getAllDrawingItems()
        },
        async turnOffOfflineMode() {
            this.signTypes = loadDefaultSignTypesFromLocalStorage()
            this.locations = loadDefaultLocationsFromLocalStorage()

            // Update selectedSignType with default signTypes(with s3 url)
            this.updateSelectedSignType()

            // Uncomment if needed
            // // Update selectedKopItemType with new kopItemTypes(with base64Img)
            // this.updateSelectedKopItemType()

            // Update selectedLocation with default locations(with s3 url)
            this.updateSelectedLocation()

            this.clearOfflineItems()
            await idbClearAllImages()
            this.revokeAllOfflineModeObjectUrls()
        },

        async handleOfflineMode() {
            this.handleOfflineModeLoading = true

            if (!this.offlineMode) {
                await this.turnOnOfflineMode()
            } else {
                await this.turnOffOfflineMode()
            }
            this.offlineMode = !this.offlineMode

            this.handleOfflineModeLoading = false
        },

        async uploadOfflineItems() {
            this.uploadOfflineSignsLoading = true

            // Prepare the payload object to send in the request
            const payload = {
                campaign: this.project.id, // ID of the current project/campaign
                offline_signs: JSON.stringify(this.offlineSigns), // Convert offline signs array to JSON string
                offline_items: JSON.stringify(this.offlineItems) // Convert offline items array to JSON string
            }

            try {
                // Send the payload to create offline signs and wait for the response
                const res = await $createOfflineItems(payload)

                this.allDefaultDrawingItems = transformDrawingsArrayToObj(res.data)

                // Update the items with the modified items returned in the response
                this.setItems(this.allDefaultDrawingItems[this.selectedLocation.id] ?? [])

                // Clear the offline signs array after successful creation
                this.offlineItems = []
                this.offlineSigns = []
                this.offlineSignLabels = []

                useSnackbarStore().showMessage({
                    color: 'success',
                    icon: 'mdi-checkbox-marked-circle',
                    title: 'Success',
                    text: 'Offline signs uploaded successfully!'
                })
            } catch (e: any) {
                // Log any errors that occur during the request
                console.log(e)
                const mes = e.response.data ?
                  (e.response.data.detail ? e.response.data.detail : JSON.stringify(e.response.data)) :
                  e.message
                useSnackbarStore().showMessage({
                    color: 'error',
                    icon: 'mdi-alert-circle',
                    title: 'Error',
                    text: `Oops, something went wrong. ${mes}`
                })
            } finally {
                this.uploadOfflineSignsLoading = false
            }
        },

        selectItem(e: any, isCustomSelect = false) {
            // isCustomSelect - Select item by code
            let currentShapeTarget = e?.target

            // Get the parent group
            const group = e?.target?.getParent()

            // Check if the parent is a group
            if (group && group.getClassName() === 'Group') {
                // Perform actions on the group
                currentShapeTarget = group
            }
            const item = isCustomSelect ? this.findItemBySign(e) : this.findItemById(currentShapeTarget.attrs.id)
            if (item && item?.type !== 'label') {

                // In KOP Item Creation Mode - can be selected only pendingKopItems
                if (this.isKopItemCreationMode) {
                    const pendingKopItemIds = this.pendingKopItems.map((kopItem: any) => kopItem.id)
                    if (!pendingKopItemIds.includes(item.id)) {
                        return
                    }
                }

                if (isCustomSelect || !e.evt.shiftKey) {
                    this.selectedItems = [item]
                } else {
                    this.selectedItems.push(item)
                    this.selectedItems = uniqBy(this.selectedItems, 'id')
                }
                this.selectedItem = item
            }

            // Show Drawing Item Config
            this.updateDrawingItemConfigVisible(true)
        },
        // Update Selected Items after Get Items
        updateSelectedItems() {
            if (this.selectedItem && this.selectedItems?.length) {
                this.selectedItem = this.items.find(i => i.id === this.selectedItem.id)
                this.selectedItems = this.items.filter(i => this.selectedItems.map((item: any) => item.id).includes(i.id))
            } else {
                this.clearSelectedItems()
            }
        },
        // Action to copy selected items
        copySelectedItems() {
            // Deep clone the selected items to avoid mutations
            this.copiedItems = this.selectedItems.map((item: any) => ({ ...item }))
        },
        // Action to paste copied items
        pasteSelectedItems() {
            if (!this.copiedItems.length) {
                return
            }

            this.clearSelectedItems()

            // Calculate the new position offsets
            const offsetX = 5 // Change X by 5 units
            const offsetY = 5 // Change Y by 5 units

            // Create pasted items with new positions and new IDs
            const pastedItems = this.copiedItems.map(item => {
                if (item?.type === 'line' && Array.isArray(item.points)) {
                    // Adjust the points array for lines
                    const newPoints = item.points.map((coord: number, index: number) =>
                      index % 2 === 0 ? coord + offsetX : coord + offsetY
                    )
                    return {
                        ...item,
                        points: newPoints,
                        id: uuidv4() // Generate a new unique ID
                    }
                } else {
                    // Adjust x and y for other shapes
                    return {
                        ...item,
                        x: (item.x ?? 0) + offsetX,
                        y: (item.y ?? 0) + offsetY,
                        id: uuidv4() // Generate a new unique ID
                    }
                }
            })

            // Add pasted items to the items array
            this.items.push(...pastedItems)

            // Replace copiedItems with the newly pasted items(with new coordinates)
            this.copiedItems = [...pastedItems]
        },
        // Action to clear copied items
        clearCopiedItems(): void {
            this.copiedItems = []
        },
        handleDragStart(e: any) {},
        handleDragEnd(e: any) {
            // Skip phantom label text dragging
            if (e?.target?.attrs?.type === 'label' && e?.target?.className === 'Text') {
                return
            }

            this.updateItemPosition(e.target)
            this.updateOfflineItemPosition(e.target)
            if (e.target?.attrs?.tag === 'anchor') {
                if (e.target?.attrs?.type === 'route-line-root-anchor') {
                    this.updateRouteRootLineAnchor(e)
                } else {
                    this.updateLineAnchor(e)
                }
            }
        },
        updateDrawingItem(e: any) {
            const pos = e.target.getStage().getPointerPosition()
            const x = (pos.x - e.currentTarget.attrs.x) / this.scale.x
            const y = (pos.y - e.currentTarget.attrs.y) / this.scale.y

            if (this.drawingItem) {
                if (this.drawingItem?.type === 'kop') {
                }

                const { updatedItem, updatedOfflineItems } = updateDrawingItem(
                  this.selectedBtn,
                  this.drawingItem,
                  x,
                  y,
                  this.offlineMode,
                  this.offlineItems,
                  this.isEditKopItemTypeShape,
                  this.initialZoom || 1,
                  e.target.getStage(),
                )
                this.drawingItem = updatedItem
                this.items.splice(this.items.length - 1, 1, this.drawingItem)

                if (this.offlineMode) {
                    this.offlineItems = updatedOfflineItems
                }
            }
        },
        async handleStageClick(e: any) {
            this.isDrawing = true
            await this.addItem(e)
        },
        selectArea(e: any) {
            this.clearSelectedItems()

            this.isDrawing = true
            const pos = e.target.getStage().getPointerPosition()

            const x = (pos.x - e.currentTarget.attrs.x) / this.scale.x
            const y = (pos.y - e.currentTarget.attrs.y) / this.scale.y
            if (this.selectedBtn === 'move') {
                this.selectedArea = {
                    id: 'drawing-map-select-area-rect',
                    type: 'rect',
                    x: x,
                    y: y,
                    fill: 'rgba(0,0,255,0.1)',
                    opacity: 1,
                    strokeWidth: 1,
                    width: 0,
                    height: 0,
                }
                this.items.push(this.selectedArea)
            }
        },
        isItemInsideSelectedArea(item: any) {
            const selectedAreaX = this.selectedArea.width >= 0 ? this.selectedArea.x : this.selectedArea.x + this.selectedArea.width
            const selectedAreaY = this.selectedArea.height >= 0 ? this.selectedArea.y : this.selectedArea.y + this.selectedArea.height
            const selectedAreaWidth = Math.abs(this.selectedArea.width)
            const selectedAreaHeight = Math.abs(this.selectedArea.height)

            // In KOP Item Creation Mode - can be selected only pendingKopItems
            if (this.isKopItemCreationMode) {
                const pendingKopItemIds = this.pendingKopItems.map((kopItem: any) => kopItem.id)
                if (!pendingKopItemIds.includes(item.id)) {
                    return false
                }
            }

            if (item?.type === 'label' || ['route', 'anchor'].includes(item?.tag)) {
                return false
            }

            if (item?.type === 'line') {
                const points = item?.points || []
                for (let i = 0; i < points.length; i += 2) {
                    const itemX = item?.x || 0
                    const itemY = item?.y || 0

                    const pointX = points[i] + itemX
                    const pointY = points[i + 1] + itemY

                    if (
                        !(
                            pointX >= selectedAreaX &&
                            pointX <= selectedAreaX + selectedAreaWidth &&
                            pointY >= selectedAreaY &&
                            pointY <= selectedAreaY + selectedAreaHeight
                        )
                    ) {
                        return false // If at least one point does not fall within the area, return false
                    }
                }

                return true // If all points fall within the area, return true
            }
            if (item?.type === 'text') {
                return (
                  item.x < selectedAreaX + selectedAreaWidth &&
                  item.x > selectedAreaX &&
                  item.y < selectedAreaY + selectedAreaHeight &&
                  item.y > selectedAreaY
                )
            }
            if (item?.type === 'circle') {
                const circleCenterX = item.x
                const circleCenterY = item.y
                const circleRadius = item.radius

                const closestX = Math.max(Math.min(circleCenterX, selectedAreaX + selectedAreaWidth), selectedAreaX)
                const closestY = Math.max(Math.min(circleCenterY, selectedAreaY + selectedAreaHeight), selectedAreaY)

                return (
                    circleCenterX >= selectedAreaX &&
                    circleCenterX <= selectedAreaX + selectedAreaWidth &&
                    circleCenterY >= selectedAreaY &&
                    circleCenterY <= selectedAreaY + selectedAreaHeight &&
                    Math.pow(circleCenterX - closestX, 2) + Math.pow(circleCenterY - closestY, 2) < Math.pow(circleRadius, 2)
                )
            }
            if (item?.type === 'triangle') {
                const triangleCenterX = item.x
                const triangleCenterY = item.y
                const triangleRadius = item.radius

                const closestX = Math.max(Math.min(triangleCenterX, selectedAreaX + selectedAreaWidth), selectedAreaX)
                const closestY = Math.max(Math.min(triangleCenterY, selectedAreaY + selectedAreaHeight), selectedAreaY)

                return (
                  triangleCenterX >= selectedAreaX &&
                  triangleCenterX <= selectedAreaX + selectedAreaWidth &&
                  triangleCenterY >= selectedAreaY &&
                  triangleCenterY <= selectedAreaY + selectedAreaHeight &&
                  Math.pow(triangleCenterX - closestX, 2) + Math.pow(triangleCenterY - closestY, 2) < Math.pow(triangleRadius, 2)
                )
            }
            if (
              item?.type === 'polygon' ||
              (item?.type === 'kop_item' && (item?.kop_item_type_form || 1) === 1)
            ) {
                const points = item?.points || [] // Array of x, y coordinates [x1, y1, x2, y2, ...]
                for (let i = 0; i < points.length; i += 2) {
                    const itemX = item?.x || 0
                    const itemY = item?.y || 0

                    const pointX = points[i] + itemX
                    const pointY = points[i + 1] + itemY

                    // Check if the point falls within the selected area
                    if (
                      pointX >= selectedAreaX &&
                      pointX <= selectedAreaX + selectedAreaWidth &&
                      pointY >= selectedAreaY &&
                      pointY <= selectedAreaY + selectedAreaHeight
                    ) {
                        return true // At least one point falls within the area
                    }
                }
                return false // No points fall within the area
            }

            const itemWidth = item?.width * item?.scaleX || 1
            const itemHeight = item?.height * item?.scaleY || 1

            if (item?.type === 'sign') {
                const filteredSignIds = this.items.filter(i => {
                    return handleWayfindingSignFilters(
                      i, this.selectedFilterSignStatuses, this.selectedFilterSignTypes,
                      this.selectedFilterSignFunctionalAreas, this.signSearchFilter, this.signSearchByFilter,
                      this.selectedSignIdsFilter
                    )
                }).map(i => i.id)

                // Only filtered signs can be selected
                if (filteredSignIds.includes(item.id)) {
                    return (
                      item?.x < selectedAreaX + selectedAreaWidth &&
                      item?.x + itemWidth > selectedAreaX &&
                      item?.y < selectedAreaY + selectedAreaHeight &&
                      item?.y + itemHeight > selectedAreaY
                    )
                }

                return false
            }

            return (
                item?.x < selectedAreaX + selectedAreaWidth &&
                item?.x + itemWidth > selectedAreaX &&
                item?.y < selectedAreaY + selectedAreaHeight &&
                item?.y + itemHeight > selectedAreaY
            )
        },
        handleStageMouseMovePolygon (e: any) {
            // If Initial Polygon Anchor - pass
            if (
              this.selectedBtn === 'polygon' &&
              e.target.attrs.id !== 'initial-polygon-anchor' &&
              this.polygon?.points?.length
            ) {
                const pos = e.target.getStage().getPointerPosition()

                const x = (pos.x - e.currentTarget.attrs.x) / this.scale.x
                const y = (pos.y - e.currentTarget.attrs.y) / this.scale.y

                this.updatePolygonNextPoint({ x: x, y: y })
            }
        },
        handleStageMouseMove(e: any) {
            this.handleStageMouseMovePolygon(e)

            if (!this.isDrawing) return
            if (this.selectedBtn === 'move') {
                if (!this.selectedArea) return

                const pos = e.target.getStage().getPointerPosition()

                const endX = (pos.x - e.currentTarget.attrs.x) / this.scale.x
                const endY = (pos.y - e.currentTarget.attrs.y) / this.scale.y

                this.selectedArea.width = endX - this.selectedArea?.x || 0
                this.selectedArea.height = endY - this.selectedArea?.y || 0
                this.items.splice(this.items.length - 1, 1, { ...this.selectedArea})
            }
            this.updateDrawingItem(e)
        },
        handleStageMouseOut(e: any) {
            if (this.selectedBtn === 'move' && this.selectedArea) {
                this.items.splice(this.items.length - 1, 1)
                this.isDrawing = false
                this.drawingItem = null
                this.selectedArea = null
            }
        },
        async handleStageMouseUp() {
            if (this.selectedBtn === 'move' && this.selectedArea) {
                this.items.forEach((item, index) => {
                    if (index !== this.items.length - 1 && this.isItemInsideSelectedArea(item)) {
                        this.selectedItems.push(item)
                        this.selectedItems = uniqBy(this.selectedItems, 'id')
                    }
                    if (this.selectedItems.length === 1) this.selectedItem = this.selectedItems[0]
                })
                this.items.splice(this.items.length - 1, 1)
            }

            // Save a deep copy of the current drawing item for further use
            const drawingItemCopy = this.drawingItem ? JSON.parse(JSON.stringify(this.drawingItem)) : null

            // If Wayfinding project map view
            if (
              this.isDrawing
              && !this.isEditKopItemTypeShape
              && !this.isSetRouteMode
              && !!this.project?.id
              && this.project?.type === 1
            ) {
                this.selectBtn('move')
            }

            this.isDrawing = false
            this.drawingItem = null
            this.selectedArea = null

            // If a KopItem Drawing item is drawn and no KopItemId is associated, create a new KopItem
            if (!!drawingItemCopy && drawingItemCopy?.type === 'kop_item' && !drawingItemCopy.kop_item) {
                const hasValidPosition = Number.isFinite(drawingItemCopy?.x) && Number.isFinite(drawingItemCopy?.y)
                const hasValidPoints = (
                  Array.isArray(drawingItemCopy?.points) &&
                  drawingItemCopy.points.length === 8 &&
                  drawingItemCopy.points.every((point: any) => Number.isFinite(point))
                )

                if (!hasValidPosition || !hasValidPoints) {
                    useSnackbarStore().showMessage({
                        color: 'warning',
                        icon: 'mdi-alert-circle',
                        title: 'Drawing KOP Item',
                        text: 'Please drag longer to create a valid KOP item. The shape requires precise coordinates.'
                    })

                    // Remove newly added KOP Item and KOP Item label (if exist) when error occurred
                    this.items = this.items.filter((item: any) => {
                        return drawingItemCopy?.id !== item?.id &&
                          !(item?.type === 'label' && drawingItemCopy?.id === item?.parent)
                    })

                    // Ensure no stale pending item remains and unlock KOP creation mode when nothing is pending.
                    this.pendingKopItems = this.pendingKopItems.filter((item: any) => item?.id !== drawingItemCopy?.id)
                    if (!this.pendingKopItems.length && !this.existingKopItemInEditMode) {
                        this.clearKopItemCreationMode()
                    }
                } else {
                    this.pendingKopItems.push(drawingItemCopy)
                }


                // TODO: KOP: remove if no need to create one piece Distance type KOP Items
                // await this.createKopItem(drawingItemCopy.id, drawingItemCopy.x, drawingItemCopy.y, {
                //     points: drawingItemCopy?.points || [],
                //     rotation: drawingItemCopy?.rotation || 0,
                //     fillPatternX: drawingItemCopy?.fillPatternX || 0,
                //     fillPatternY: drawingItemCopy?.fillPatternY || 0,
                // })
            }
        },
        handleTransformStart(e: any) {
            // Get the current target node
            const node = e.target

            this.transformStartScale.x = node.scaleX() || 1
            this.transformStartScale.y = node.scaleY() || 1
        },
        handleTransformed(e: any) {
            // Checking and limiting the boundaries of the PDF area
            const node = e.target
            // Get the current stage instance
            const currentStage = this.stage?.getStage()

            const box = node.getClientRect()

            const absPos = node.getAbsolutePosition()
            const offsetX = box.x - absPos.x
            const offsetY = box.y - absPos.y

            const newAbsPos = {...absPos}
            if (box.x - currentStage.x() < 0) {
                newAbsPos.x = -offsetX + currentStage.x()
            }
            if (box.y - currentStage.y() < 0) {
                newAbsPos.y = -offsetY + currentStage.y()
            }
            if (box.x - currentStage.x() + box.width > this.width) {
                newAbsPos.x = this.width - box.width - offsetX + currentStage.x()
            }
            if (box.y - currentStage.y() + box.height > this.height) {
                newAbsPos.y = this.height - box.height - offsetY + currentStage.y()
            }
            node.setAbsolutePosition(newAbsPos)

            // Find and update item data
            const item = this.findItemById(node.attrs.id)

            if (item?.type === 'line') {
                const oldPoints = node.points()
                const newPoints = []
                for(let i = 0; i< oldPoints.length / 2; i++) {
                    const point = {
                        x: oldPoints[i * 2] * node.scaleX(),
                        y: oldPoints[i * 2 + 1] * node.scaleY(),
                    }
                    newPoints.push(point.x, point.y)
                }
                node.points(newPoints)
                node.scaleX(1)
                node.scaleY(1)
                // layer.draw()

                item.points = node.points()
            } else if (item?.type === 'kop_item') {
                const oldPoints = node.points()
                const newPoints = []

                // Get the current total scale (including parent scales if any)
                const currentScaleX = node.scaleX()
                const currentScaleY = node.scaleY()
                // Get the initial scale we want to return to (default to 1 if not set)
                const targetScaleX = this.transformStartScale.x || 1
                const targetScaleY = this.transformStartScale.y || 1

                // Calculate how much we need to multiply the points by
                // This is the ratio of current scale to target scale
                const scaleFactorX = currentScaleX / targetScaleX
                const scaleFactorY = currentScaleY / targetScaleY

                for(let i = 0; i< oldPoints.length / 2; i++) {
                    const point = {
                        x: oldPoints[i * 2] * (scaleFactorX),
                        y: oldPoints[i * 2 + 1] * (scaleFactorY),
                    }
                    newPoints.push(point.x, point.y)
                }
                node.points(newPoints)
                node.scaleX(targetScaleX)
                node.scaleY(targetScaleY)

                item.points = node.points()
            }

            item.x = node.x()
            item.y = node.y()
            item.rotation = node.rotation()
            item.scaleX = node.scaleX()
            item.scaleY = node.scaleY()

            if (['sign', 'kop_item'].includes(item?.type)) {
                item.tagX = item.x + 10
                item.tagY = item.y + 25
            }
        },
        lockMoving() {
            const isMoveMode = this.isMoveMode

            this.items = this.items.map((item: any) => ({...item, draggable: isMoveMode}))
            if (this.selectedItems) {
                this.selectedItems = this.selectedItems.map((item: any) => ({...item, draggable: isMoveMode}))
            }
            if (this.selectedItem) {
                this.selectedItem.draggable = this.isMoveMode
            }
        },

        // HISTORY (UNDO/REDO)
        // Save drawingHistory
        saveHistory(changedItems: DrawingHistoryItem[]) {
            // Create a snapshot with id, position and size
            const snapshot = changedItems.map(item => {
                const result: any = { id: item.id }

                if (item.x !== undefined) result.x = item.x
                if (item.y !== undefined) result.y = item.y
                if (item.realWidthLabelX !== undefined) result.realWidthLabelX = item.realWidthLabelX
                if (item.realWidthLabelY !== undefined) result.realWidthLabelY = item.realWidthLabelY
                if (item.kopItemCodeLabelX !== undefined) result.kopItemCodeLabelX = item.kopItemCodeLabelX
                if (item.kopItemCodeLabelY !== undefined) result.kopItemCodeLabelY = item.kopItemCodeLabelY
                if (item.width !== undefined) result.width = item.width
                if (item.height !== undefined) result.height = item.height
                if (item.scaleX !== undefined) result.scaleX = item.scaleX
                if (item.scaleY !== undefined) result.scaleY = item.scaleY
                if (item.rotation !== undefined) result.rotation = item.rotation
                if (item.tagX !== undefined) result.tagX = item.tagX
                if (item.tagY !== undefined) result.tagY = item.tagY

                return result
            })

            // Step 1: Remove any forward history
            this.drawingHistory = this.drawingHistory.slice(0, this.drawingHistoryStep)

            // Step 2: Add the new snapshot of changed items to the history
            this.drawingHistory.push(snapshot)

            // Step 3: Update the history step
            this.drawingHistoryStep += 1

            // Step 4: If history exceeds 30 steps, remove the oldest snapshot
            if (this.drawingHistory.length > 30) {
                this.drawingHistory.shift()  // Remove the first element
                this.drawingHistoryStep -= 1 // Adjust history step to account for the removed snapshot
            }
        },

        // Undo action
        undo() {
            // Step 1: Only allow undo if we're not at the first history step
            if (this.drawingHistoryStep > 0) {
                // Step 2: Update the history index to reflect the undo
                this.drawingHistoryStep -= 1

                // Move one step back in the history
                const previousState = this.drawingHistory[this.drawingHistoryStep] as DrawingHistoryItem[]

                // Step 3: Apply the previous state to the current items
                previousState.forEach((savedItem: DrawingHistoryItem) => {
                    // Find the corresponding item in current state by ID
                    const currentItem = this.items.find((item: any) => item.id === savedItem.id)

                    // Skip if item no longer exists in current state
                    if (!currentItem) return

                    // Process each property we want to potentially update/delete
                    (
                      ['x', 'y', 'width', 'height', 'scaleX', 'scaleY', 'rotation', 'tagX', 'tagY'] as const)
                      .forEach(prop => {
                        try {
                            // Check if the property exists and is defined in saved state
                            if (savedItem[prop] !== undefined) {
                                // Update current item's property with saved value
                                currentItem[prop] = savedItem[prop]!
                            } else if (prop in currentItem) {
                                // Property is undefined in saved state - delete it from current item
                                delete currentItem[prop]
                            }
                        } catch {
                            // Silently skip any errors
                        }
                      }
                    )

                    this.updateSelectedItems()
                })
            }
        },

        // Redo action
        redo() {
            // Step 1: Only allow redo if we're not at the latest point in history
            if (this.drawingHistoryStep < this.drawingHistory.length - 1) {
                // Step 2: Update the history index to reflect the redo
                this.drawingHistoryStep += 1

                // Move one step forward in the history
                const nextState = this.drawingHistory[this.drawingHistoryStep] as DrawingHistoryItem[]

                // Step 3: Apply the next state to the current items
                nextState.forEach((savedItem: DrawingHistoryItem) => {
                    // Find the corresponding item in current state by ID
                    const currentItem = this.items.find((item: any) => item.id === savedItem.id)

                    // Skip if item no longer exists in current state
                    if (!currentItem) return

                    // Process each property we want to potentially update/delete
                    (
                      ['x', 'y', 'width', 'height', 'scaleX', 'scaleY', 'rotation', 'tagX', 'tagY'] as const)
                      .forEach(prop => {
                            try {
                                // Check if the property exists and is defined in saved state
                                if (savedItem[prop] !== undefined) {
                                    // Update current item's property with saved value
                                    currentItem[prop] = savedItem[prop]!
                                } else if (prop in currentItem) {
                                    // Property is undefined in saved state - delete it from current item
                                    delete currentItem[prop]
                                }
                            } catch {
                                // Silently skip any errors
                            }
                        }
                      )

                    this.updateSelectedItems()
                })
            }
        },

        toggleGridMode(isEnabled: boolean) {
            this.gridModeSettings.isEnabled = isEnabled
        },

        incrementGridOpacity() {
            // Increase the opacity by 0.05, but ensure it doesn't exceed 1
            if (this.gridModeSettings.gridOpacity < 1) {
                this.gridModeSettings.gridOpacity = Math.min(this.gridModeSettings.gridOpacity + 0.05, 1)
            }
        },

        decrementGridOpacity() {
            // Decrease the opacity by 0.05, but ensure it doesn't go below 0
            if (this.gridModeSettings.gridOpacity > 0) {
                this.gridModeSettings.gridOpacity = Math.max(this.gridModeSettings.gridOpacity - 0.05, 0)
            }
        },

        // Group selected items
        async groupItems() {
            if (this.selectedItems.length < 2) {
                useSnackbarStore().showMessage({
                    color: 'warning',
                    icon: 'mdi-alert-circle',
                    title: 'Warning',
                    text: `Please select at least two items to group.`
                })
                return
            }

            // Calculate the bounding box of the selected items
            let minX = Infinity
            let minY = Infinity
            let maxX = -Infinity
            let maxY = -Infinity

            // Helper for regular items (rectangle-like)
            const updateBounds = (x: number, y: number, width: number, height: number) => {
                minX = Math.min(minX, x)
                minY = Math.min(minY, y)
                maxX = Math.max(maxX, x + width)
                maxY = Math.max(maxY, y + height)
            }

            // Helper for line/shape with points
            const updateBoundsFromPoints = (points: number[], x: number, y: number, scaleX = 1, scaleY = 1) => {
                for (let i = 0; i < points.length; i += 2) {
                    const px = x + points[i] * scaleX
                    const py = y + points[i + 1] * scaleY
                    minX = Math.min(minX, px)
                    minY = Math.min(minY, py)
                    maxX = Math.max(maxX, px)
                    maxY = Math.max(maxY, py)
                }
            }

            // Loop through selected items
            this.selectedItems.forEach((item: any) => {
                const scaleX = item.scaleX || 1
                const scaleY = item.scaleY || 1
                const x = item.x || 0
                const y = item.y || 0

                // Regular Konva item
                if (!item?.type?.startsWith('kop_item')) {
                    const width = (item.width || 0) * scaleX
                    const height = (item.height || 0) * scaleY
                    updateBounds(x, y, width, height)

                    if (item.points && Array.isArray(item.points))
                        updateBoundsFromPoints(item.points, x, y, scaleX, scaleY)

                    return
                }

                // Handle kop_item type Konva item
                if (item?.type === 'kop_item' && Array.isArray(item.children)) {
                    const parentX = x
                    const parentY = y

                    item.children.forEach((child: any) => {
                        const cScaleX = child.scaleX || 1
                        const cScaleY = child.scaleY || 1
                        const childX = parentX + (child.x || 0)
                        const childY = parentY + (child.y || 0)

                        if (child?.points && Array.isArray(child?.points)) {
                            // Distance KOP Item (Closed line shape)
                            updateBoundsFromPoints(child.points, childX, childY, cScaleX, cScaleY)
                        } else {
                            // Individual KOP Item (Rectangle-like shape)
                            const cWidth = (child.kop_item_shape_width || 0) * cScaleX
                            const cHeight = (child.kop_item_shape_height || 0) * cScaleY
                            updateBounds(childX, childY, cWidth, cHeight)
                        }
                    })
                }
            })

            // Calculate the bounding box dimensions
            const groupWidth = maxX - minX
            const groupHeight = maxY - minY

            const groupId = uuidv4()

            // Create the group with the correct x and y coordinates
            const group: any = {
                id: groupId,
                type: 'group',
                children: [],
                opacity: 1,
                scaleX: 1,
                scaleY: 1,
                x: minX,
                y: minY,
                width: groupWidth,
                height: groupHeight,
                draggable: this.selectedBtn || false
            }

            // Adjust the children's x and y coordinates relative to the group's position
            this.selectedItems.forEach((item: any) => {
                const adjustedItem: any = { ...item }

                // if (adjustedItem.x !== undefined && adjustedItem.y !== undefined) {
                //
                // }
                adjustedItem.x = (adjustedItem.x || 0) - minX
                adjustedItem.y = (adjustedItem.y || 0) - minY
                adjustedItem.draggable = false

                // Konva text in group issue (x and y not 0)
                if (adjustedItem?.type === 'text') {
                    adjustedItem.x = adjustedItem?.x === 0 ? 0.00001 : adjustedItem?.x
                    adjustedItem.y = adjustedItem?.y === 0 ? 0.00001 : adjustedItem?.y
                }

                // if (adjustedItem.points && Array.isArray(adjustedItem.points)) {
                //     for (let i = 0; i < adjustedItem.points.length; i += 2) {
                //         adjustedItem.points[i] -= minX
                //         adjustedItem.points[i + 1] -= minY
                //     }
                // }

                group.children.push(adjustedItem)
            })

            const selectedItemIds = this.selectedItems.map((item: any) => item.id)

            // Add the new group
            this.items.push(group)

            // Remove individual selected items from the store after group create
            this.items = this.items.filter((item: any) => !selectedItemIds.includes(item.id))

            // Clear selected items after grouping
            this.clearSelectedItems()

            // If Wayfinding/KOP project map view - save group action + new/unsaved items
            if (!!this.project?.id && [1, 5].includes(this.project?.type)) {
                const res = await this.saveItems([group])
                this.setItems(res?.data?.modified_items || [])
            }
        },

        // Ungroup a selected group
        async ungroupItem() {
            const newGroupItems: any = []

            const selectedItem = this.selectedItems[0]

            if (!selectedItem || selectedItem?.type !== 'group') {
                useSnackbarStore().showMessage({
                    color: 'warning',
                    icon: 'mdi-alert-circle',
                    title: 'Warning',
                    text: `Please select a group to ungroup.`
                })
                return
            }

            // Add the group's children back as individual items
            selectedItem.children.forEach((child: any) => {
                const x = (typeof child.x === 'number' && !isNaN(child.x) ? child.x : 0) +
                  (typeof selectedItem.x === 'number' && !isNaN(selectedItem.x) ? selectedItem.x : 0)

                const y = (typeof child.y === 'number' && !isNaN(child.y) ? child.y : 0) +
                  (typeof selectedItem.y === 'number' && !isNaN(selectedItem.y) ? selectedItem.y : 0)

                const updatedItem = {
                    ...child,
                    scaleX: (child.scaleX || 1) * (selectedItem.scaleX || 1),
                    scaleY: (child.scaleY || 1) * (selectedItem.scaleY || 1),
                    x,
                    y
                }

                this.items.push(updatedItem)
                newGroupItems.push(updatedItem)
            })

            // Remove the group from the store
            this.items = this.items.filter(item => selectedItem.id !== item.id)

            // Clear selected items after ungrouping
            this.clearSelectedItems()

            // If Wayfinding project map view
            // If Wayfinding/KOP project map view - save ungroup action + new/unsaved items
            if (!!this.project?.id && [1, 5].includes(this.project?.type)) {
                const res = await this.saveItems(newGroupItems)
                this.setItems(res?.data?.modified_items || [])
            }
        }
    }
})

// Define the type of the store
export type DrawingStore = ReturnType<typeof useDrawingStore>
